/**
 * UploadView.tsx — "Nuovo progetto": upload, what we found inside it, and the
 * analysis progress surface.
 *
 * The run is server-side (BackgroundTasks on Railway): closing the laptop or
 * losing wifi does not affect it, and THIS page shows progress, estimated time
 * and any failure.
 *
 * `P-31`, 2026-08-19: the analysis no longer auto-starts. Omar amended point 3
 * of the 2026-07-23 doctrine after reviewing a prototype — the user confirms the
 * computo documents (*Passo 1 di 2*), chooses what enters the Knowledge Base
 * (*Passo 2 di 2*), and presses **Avvia**. The amendment is narrower than it
 * looks and points the same way: the doctrine's stated purpose is that
 * "incomplete input never silently spends money", and a confirmation serves that
 * BETTER than the pre-flight did, because a 422 can only catch a document that
 * is MISSING, never one that is MISCLASSIFIED — a CME read as an Elenco Prezzi
 * passes every pre-flight and wastes the whole 1-3 hour run. Points 1, 2, 4 and
 * 5 are untouched: server-side, disconnect-safe, rediscovered on reload,
 * notifies on completion. What changed is who presses go, not what happens
 * after. Re-run buttons remain banned; retry still appears only from a failure.
 *
 * Rebuilt 2026-08-14 around three phases the user can name, because the old
 * single-phase screen had one failure that made all the others invisible: after
 * dropping a ZIP, nothing on screen changed. The bundle was ONE row called
 * `something.zip`, the ~30 documents the server had already classified were
 * discarded, and the primary button read "Crea progetto" — bookkeeping language
 * for an action that also uploads, expands, classifies and launches a 1–3 hour
 * paid run.
 *
 *   FASE 1  Carica e riconosci   → staging, then one action that does the lot
 *   FASE 2  Conferma e avvia      → Passo 1 di 2 the computo documents · Passo
 *                                  2 di 2 the Knowledge Base · then ONE button.
 *                                  Once started: the two tracks side by side,
 *                                  the analysis running and the KB on its own
 *                                  clock, neither waiting on the other
 *   FASE 3  Esito                → what the run actually concluded, and where to go
 *
 * The two tracks are drawn as two things precisely so "did I miss a button?" is
 * answerable by reading. The ratified rules survive intact: the analysis is
 * never gated on the KB decision, and the KB is never written from the browser.
 *
 * Resilience details, all load-bearing and all pre-existing: the created project
 * id is kept in state so a retry after a partial upload failure never creates a
 * duplicate project; per-file upload state lets only the failed files re-send; a
 * missing required role blocks the start with an explicit Italian list.
 */
import { useEffect, useRef, useState, type DragEvent, type KeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";

import { I, type IconComponent } from "../../components/icons";
import { AdminOnlyDeleteDialog } from "../../components/ui/AdminOnlyDeleteDialog";
import { ApiError, apiErrorMessage } from "../../lib/api";
import {
  filesFromDataTransfer,
  folderPathOf,
  stagedKey,
  type DroppedFile,
} from "../../lib/dropTree";
import {
  useCanApproveKb,
  useCanDeleteDocument,
  useCapability,
  useKbProjectDocuments,
} from "../../lib/kbApi";
import { KB_PROGRESS_UNAVAILABLE, kbSynthesisProgress } from "../../lib/kbProgress";
import { type UploadFileType } from "../../lib/mock";
import {
  categoryReconciliationOf,
  DOC_ROLE_LABEL,
  guessRole,
  missingRolesFromError,
  reconciliationOf,
  SHOW_RECONCILIATION_ON_COMPLETION,
  useAnalysisJob,
  useCreateProject,
  useDeleteDocument,
  useProjectDocuments,
  useAddProjectPriceList,
  usePriceListLibrary,
  useProjectPriceLists,
  useRemoveProjectPriceList,
  useStartAnalysis,
  useUploadArchive,
  useUploadDocument,
  userWorkRefusalFromError,
  type AnalysisJob,
  type UserWorkRefusal,
  type TenderDocRole,
  type TenderDocument,
  type TenderProject,
  useActiveImportJob,
  archiveImportCounts,
} from "../../lib/tenderApi";
import { useDocumentCorrection } from "../../lib/useDocumentCorrection";
import { useDocumentDownload, usePriceListFileDownload } from "../../lib/useDocumentDownload";
import { plural, useKbDecision } from "../../lib/useKbDecision";
import { fileToUploadFile, useFilePicker } from "../../lib/useFilePicker";
import { failureLine, isSelfContainedFailure } from "../../lib/jobFailure";
import { IntakeReviewPanel } from "./IntakeReviewPanel";

/**
 * What to tell the user about the Knowledge Base after an upload.
 *
 * Deliberately NOT "aggiunta alla Knowledge Base ✓", which is what the browser
 * path used to claim. Nothing has been added: intake has PROPOSED documents and
 * an administrator has to approve them before a single one is synthesised
 * (Omar, 2026-08-12 — a tender bundle is ~18 documents and ~1.6M input tokens,
 * and that is not spent on his behalf without him seeing the list).
 *
 * Saying "added" here would be the most expensive kind of wrong: the user stops
 * looking, and the documents sit unapproved forever.
 */
export function kbProposedNote(proposed: number): string | null {
  if (proposed === 0) return null;
  return proposed === 1
    ? "1 documento proposto per la Knowledge Base — un amministratore deve approvarlo."
    : `${proposed} documenti proposti per la Knowledge Base — un amministratore deve approvarli.`;
}

const FILE_ICON: Record<UploadFileType, IconComponent> = {
  pdf: I.PDF,
  xlsx: I.Excel,
  dwg: I.DWG,
  other: I.File,
};
/* `P-39` `F3`/`L5-05` — `FILE_COLOR` is DELETED, not re-pointed at tokens.
   It tinted each staged row's glyph by file extension, and two of the three
   literals were this design's semantic tokens spelled out: `#B53D2A` is
   `--c-err` and `#2F7D4E` is `--c-ok`. So a staged PDF was drawn in the
   product's error colour and a spreadsheet in its success colour, on a list
   where a real failure ALSO prints in red — a row could read as broken because
   of its extension. The prototype gives every file the same pale-blue tile and
   navy glyph, ZIP included, and says nothing with colour that it does not mean.
   The extension is already stated by the icon shape and by the filename. */

/* `P-37`/A7 — the three phases of the screen, in the prototype's own words.
   A tuple rather than a bare string so a future subtitle has somewhere to go
   without a second array falling out of sync with this one. */
/* The drop target's accessible name. One constant because the zone has TWO
   shapes (`P-37`/A5) and a screen-reader user, or a test, must be able to reach
   "the place files go" without knowing which one is currently rendered. It had
   no accessible name at all before: a bare <div> with drag handlers is, to a
   screen reader, nothing. */
const DROPZONE_LABEL = "Aggiungi documenti al progetto";

/**
 * R5 D-3 — phase 2's label depends on which HALF of phase 2 this is.
 *
 * It was the constant "Analisi in corso", and the stepper marks `n === phase`
 * as current — so on the confirm-and-start screen the brand-coloured step said
 * the analysis was running while the eyebrow six lines below said *"conferma e
 * avvia"*, the track card said *"Non ancora partita"* and the footer said
 * *"Niente è ancora partito"*. `P-31`'s entire premise is that nothing starts by
 * itself, and the one element asserting otherwise was `aria-hidden`, so no
 * accessible-name query in 808 tests could ever have seen it. Position and
 * colour are claims.
 */
export function phaseSteps(started: boolean, phase: 1 | 2 | 3): string[] {
  /* Step 2's label is a claim about the present, so it has to follow the phase
     as well as the run. `phase === 3` implies `jobId` is set, so keying on
     `started` alone printed "Analisi in corso" on the ESITO screen — beside
     «Analisi non riuscita», with the circle brand-filled (`done || here`), for
     the whole of Fase 3. In Fase 3 step 2 is a step we have LEFT; name it for
     what it was and let step 3 carry the outcome, which is its job. */
  const middle = phase === 3 ? "Analisi" : started ? "Analisi in corso" : "Conferma e avvia";
  return ["Carica e riconosci", middle, "Esito"];
}

const PIPELINE_STEPS: [string, string][] = [
  ["Estrazione voci CME", "Excel & PDF strutturati"],
  ["OCR listini prezzi", "Confronto regionale"],
  ["Calcolo costi iniziali", "Aggregazione per categoria"],
  ["Rilevazione discrepanze", "Quantità · prezzi · descrizioni"],
];

/**
 * Map job progress (0-100) to the active pipeline step index (0-3).
 *
 * Index 2 used to be unreachable — the thresholds went 60 → 85 → 100 and
 * returned 0, 1, 3, 4 — so "Calcolo costi iniziali" rendered pending and then
 * jumped straight to ✓ without ever being the running step. A checklist with a
 * step that can never be in progress is a checklist that is lying about one
 * quarter of the run.
 */
/**
 * Why a run cannot start — or `null` when it can.
 *
 * One predicate, three consumers: the `disabled` of every control that reaches
 * `startRun`, the sentence printed beside them, and `startRun`'s own first
 * line. They used to be three separate expressions and drifted twice — round 1
 * shipped an Avvia that was live mid-upload, round 2 found a second button
 * still open after the first was fixed, and the round-1 label fix silently
 * deleted a sentence because the boolean and the copy read different flags.
 *
 * Exported because it is the only way this logic can be tested where it is not
 * hidden behind a `disabled` attribute: a click on a disabled button never
 * reaches the handler, so an in-component check has no reachable failing state
 * and a suite can stay green with it deleted.
 *
 * ORDER IS THE ANSWER, not just precedence. A Reviewer is told about their
 * role, not sent to hunt for a file; an upload in flight is a thing to wait
 * for, an unsent file is a thing to press.
 */
export type StartBlock = "role" | "uploading" | "side-upload" | "unsent" | "listino" | null;

/**
 * `busy` covers only `submit()`'s own loop. Two later upload paths — the
 * missing-required-document row (`P-43`) and the listino card (`P-44`) — set
 * their own private latches, and Avvia could not see either: picking a listino
 * on Passo 1 and pressing Avvia on Passo 2 started a real 1–3 hour paid run
 * that staged NO listino, after which the locked card listed the file that
 * landed a second later under «Usati per il confronto prezzi».
 *
 * CORRECTED 25 Aug. This paragraph used to end "The server does not catch it,
 * because `regional` is an OPTIONAL analyze role — there is no 422 to self-heal
 * from." `P-47` made the listino mandatory, so the server DOES catch it now and
 * there IS a 422. The guard is still worth having, for the reason the sentence
 * originally gave and not the one it claimed: a run refused at the server is a
 * round trip and an error banner, where this is a button that simply waits.
 */
export function sideUploadSentence(
  listino: { name: string } | null,
  missing: { name: string } | null,
): string {
  // Two different things are being waited for and they are not the same
  // sentence: one is an OPTIONAL price list, the other a REQUIRED document
  // without which the analysis cannot start at all. Naming the file is what
  // makes the wait legible rather than a dead button.
  if (missing) {
    return `Stiamo ancora caricando «${missing.name}»: l'analisi parte quando è arrivato.`;
  }
  if (listino) {
    return `Stiamo ancora caricando il listino «${listino.name}»: l'analisi parte quando è arrivato, così il confronto prezzi lo include.`;
  }
  return "Stiamo ancora caricando un file: l'analisi parte quando è arrivato.";
}

/**
 * Why a price-list change failed, in Italian, to the person who tried.
 *
 * `P-47b`. The server's `detail` is English and internal («edition has no price
 * workbook»), and the refusals a user can actually cause deserve different
 * sentences: one is about the edition, one is about them, and the rest is a
 * retry. Flattening them into one "something went wrong" is the shape that
 * sends people to support.
 *
 * ONE function for BOTH directions, and that is the fix rather than a tidy-up.
 * The independent QA round found the add path (repaired earlier in this same
 * round) naming the role on a 403 while the remove path still said «Ricarica la
 * pagina e riprova» — advice that can never help a Reviewer, whose role will
 * refuse them again after every reload. Two sibling handlers, one improved,
 * the other left behind: a `403` is a fact about the CALLER, so it cannot
 * depend on which button they pressed. The verb is the only thing that may
 * differ, so the verb is the only thing parameterised.
 *
 * Exported so the mapping is testable without driving the whole wizard — a
 * message chosen inside a nested JSX ternary has no failing state a test can
 * reach, which is exactly how the missing arm stayed missing.
 */
export function priceListMessage(err: unknown, action: "add" | "remove"): string {
  const status = err instanceof ApiError ? err.status : null;

  // TWO different 409s come off this endpoint and they mean opposite things, so
  // the status alone is not enough to answer with. Keying on the code alone
  // told a user whose project is LOCKED that their chosen listino «has no
  // usable price file» — a confident, specific, wrong statement about the wrong
  // subject. The server's `detail.message` is the discriminator; it is English
  // and internal, so it decides the branch and never reaches the screen.
  const detail =
    err instanceof ApiError ? (err.body as { detail?: { message?: unknown } })?.detail : null;
  const serverMessage = typeof detail?.message === "string" ? detail.message : "";

  if (status === 409 && serverMessage.startsWith("analysis already started")) {
    return (
      "L'analisi di questo progetto è già partita, quindi i listini non si possono più " +
      "cambiare: cambiarli ora non corrisponderebbe ai prezzi già calcolati. Ricarica la " +
      "pagina per vedere lo stato aggiornato."
    );
  }
  if (status === 409 && action === "add") {
    return (
      "Questo listino non ha nessun file di prezzi utilizzabile, quindi non cambierebbe " +
      "l'analisi. Scegline un altro dalla libreria, oppure carica il tuo file .xlsx."
    );
  }
  if (status === 403) {
    return "Il tuo ruolo non permette di modificare i listini di questo progetto. Chiedi a un amministratore o a un editor del tuo studio.";
  }
  return action === "add"
    ? "Non siamo riusciti ad associare il listino. Riprova; se il problema resta, ricarica la pagina."
    : "Non siamo riusciti a rimuovere il listino. Riprova; se il problema resta, ricarica la pagina.";
}

/** Kept as the name the `P-47b` tests and call site already use. */
export function addPriceListMessage(err: unknown): string {
  return priceListMessage(err, "add");
}

export function startBlockedBy(s: {
  canRunAnalysis: boolean;
  busy: boolean;
  sideUploading: boolean;
  stagedPending: number;
  /** `P-47` — no price book, and we KNOW it (never merely "not loaded yet"). */
  missingPriceBook: boolean;
}): StartBlock {
  if (!s.canRunAnalysis) return "role";
  if (s.busy) return "uploading";
  if (s.sideUploading) return "side-upload";
  if (s.stagedPending > 0) return "unsent";
  // LAST, and the order is the answer here as much as above. Every state before
  // this one is in flight: a listino still uploading, or a staged file not yet
  // sent, may BE the price book. Telling someone a listino is missing while
  // theirs is mid-upload is a false statement the screen would then have to
  // retract a second later.
  if (s.missingPriceBook) return "listino";
  return null;
}

/**
 * The badge on a STEP tab (Passo 1 / Passo 2) once a run exists.
 *
 * It was `kbBroke = id === "2" && !awaitingStart && !!kb.error` and otherwise a
 * green ✓ — so `kb.error` was the only input that could make either tab claim
 * anything but success, and `id === "2"` meant tab 1 could not take that branch
 * at all. The reachable consequence: a `member` starts a run, `startRun` never
 * submits the batch because they lack `kb:approve`, every document stays
 * `pending`, and "Passo 2 Knowledge Base" carries a green tick beside a card
 * reading "N documenti in attesa di conferma da un amministratore".
 *
 * A tick is a claim and colour is a claim — the file already says so, one
 * branch over, for the case it did fix. Where there is no outcome, the badge
 * shows the step NUMBER and no colour: an outline circle claims nothing.
 *
 * Exported because a badge chosen inside a nested JSX ternary, on an element
 * whose glyph matches no accessible-name query, has no failing state any test
 * can reach — `grep "kbBroke\|badgeTone" __tests__/` returned nothing at all.
 */
export function stepTabBadge(s: {
  id: "1" | "2";
  kbError: boolean;
  /** Documents still awaiting a KB decision — `optional + lockedIn`. */
  kbAwaiting: number;
  /** Documents whose forward to the Knowledge Base FAILED server-side. */
  kbFailed: number;
  /** Documents the server has moved out of `pending`, in either direction. */
  kbDecided: number;
  /** Required roles the LAST start attempt reported missing. */
  missingRequired: number;
}): { badge: string; tone: string; filled: boolean } {
  const NEUTRAL = { tone: "var(--c-line-strong)", filled: false };
  if (s.id === "2") {
    // A client-side refusal of the batch.
    if (s.kbError) return { badge: "!", tone: "var(--c-err)", filled: true };
    // Nothing decided yet — not a failure, and not a success either.
    if (s.kbAwaiting > 0) return { badge: "2", ...NEUTRAL };
    // Decided, and the server could not forward them. This is the case the green
    // tick was still claiming success on: `kb.error` is a CLIENT-side rejection
    // of the request, and a batch that was accepted and then failed downstream
    // never sets it. Every document could have failed and the tab said ✓.
    if (s.kbFailed > 0) return { badge: "!", tone: "var(--c-err)", filled: true };
    // Nothing was ever eligible, so there is no outcome to report. Report none
    // rather than ticking an errand nobody ran.
    //
    // ORDER IS LOAD-BEARING, and these two inputs are not independent: "failed"
    // is in `DECIDED_KB_STATES`, so `kbFailed > 0` implies `kbDecided > 0` and
    // 16 of the 64 input tuples cannot occur. Move this `if` above the one for
    // `kbFailed` and a batch where every forward failed prints the neutral "2"
    // instead of the red "!". The 64-cell table in `intakePredicates.qa.test.ts`
    // is what makes that swap red; before it existed nothing would have caught it.
    if (s.kbDecided === 0) return { badge: "2", ...NEUTRAL };
    return { badge: "✓", tone: "var(--c-ok)", filled: true };
  }
  // Tab 1's tick really is about the confirmation, and the server accepted these
  // documents — but a refusal since then means the set is no longer complete,
  // and the panel below prints a warn row saying so. Agreeing with it costs one
  // branch; a green tick over it is the same class of claim as tab 2's.
  if (s.missingRequired > 0) return { badge: "!", tone: "var(--c-warn)", filled: true };
  return { badge: "✓", tone: "var(--c-ok)", filled: true };
}

/** What the bar under a staged row is measuring right now (`P-48`). */
export type RowProgressPhase = "sending" | "accepted" | "importing" | "saving";

/**
 * The highest this bar goes while the import is still running.
 *
 * The server caps `Job.progress` at 99 for a stated reason — «a bar that
 * reaches 100% while the documents are still uncommitted is a claim the
 * database has not yet agreed to». That cap had NO CONSUMER: the client never
 * reads `job.progress`, it computes the percentage from `documents_imported /
 * documents_total`, which is published when the last member finishes STAGING —
 * before a single row is written, before the transaction commits, and (because
 * `_stage_entry` reports from a `finally`) even for a member that RAISED.
 *
 * Two independent reviews landed on this line from different directions. The
 * number is now capped where it is actually rendered, so the guarantee has a
 * consumer at last.
 */
export const IMPORT_BAR_MAX = 99;

export interface RowProgress {
  phase: RowProgressPhase;
  /** 0-100, and always ABOUT `phase` -- never a blend of two subjects. */
  value: number;
}

/**
 * The bar under one staged row, from the two things that can be known about it.
 *
 * Pure and exported because jsdom does not lay anything out, so no test can say
 * whether the bar LOOKS right -- but every claim that matters here is arithmetic
 * and can be pinned exactly: which subject is being measured, and what fraction
 * of it is done.
 *
 * The bar deliberately RESTARTS when the subject changes from bytes to
 * documents. A single bar spanning both would have to weight them against each
 * other, and the weight would be a fiction: the upload takes as long as the
 * user's connection decides and the import took 51,1 s on a 67 MB bundle, with
 * no fixed relationship between them. The label changes in the same instant
 * («caricamento… 100%» to «apertura e riconoscimento… 12 di 30 documenti»), so
 * what the reader sees is a second phase starting, not a bar losing its place.
 *
 * `accepted` -- sent, but the server has published no count yet -- reports 0
 * rather than carrying 100 over from the upload. Holding at 100 is precisely
 * the frozen full bar this item exists to remove.
 *
 * `saving` -- every member is staged and the server is writing the rows. A
 * DISTINCT phase rather than a full bar, because "all documents recognised" and
 * "the import succeeded" are different claims and the gap between them is where
 * an import can still fail and roll everything back.
 */
export function archiveRowProgress(
  uploadPct: number | undefined,
  counts: { imported: number; total: number } | null | undefined,
): RowProgress {
  if (counts === undefined) return { phase: "sending", value: uploadPct ?? 0 };
  if (counts === null) return { phase: "accepted", value: 0 };
  if (counts.total <= 0) return { phase: "accepted", value: 0 };
  if (counts.imported >= counts.total) return { phase: "saving", value: IMPORT_BAR_MAX };
  return {
    phase: "importing",
    value: Math.min(IMPORT_BAR_MAX, Math.round((counts.imported / counts.total) * 100)),
  };
}

export function activePipelineStep(progress: number): number {
  if (progress < 60) return 0; // staging + Phase-1 extraction (the long one)
  if (progress < 85) return 1; // Phase-2 matching vs listini
  if (progress < 93) return 2; // cost aggregation per category
  if (progress < 100) return 3; // parse + persist + discrepanze
  return 4; // done — every step shows ✓
}

/** The order the role <select> presents the pipeline roles. */
const ROLE_OPTIONS: TenderDocRole[] = [
  "cme",
  "full_price",
  "np",
  "info",
  "regional",
  "drawing",
  "other",
];

type FileState = "staged" | "uploading" | "uploaded" | "failed";

/** A file staged for upload, with the pipeline role it will be sent as. */
interface StagedFile {
  /**
   * Identity within the staging list: the folder plus the filename.
   *
   * It used to be the bare filename, which silently discarded the second of two
   * same-named files in different folders — and a real tender download has
   * exactly that (`01.Documenti generali/Relazione.pdf` beside
   * `04.PSC/Relazione.pdf`). Every lookup below keys on this, not on file.name.
   */
  key: string;
  file: File;
  /** Directory inside the uploaded bundle, `""` at the root. */
  folderPath: string;
  role: TenderDocRole;
  state: FileState;
}

function isArchive(file: File): boolean {
  return /\.zip$/i.test(file.name);
}

/** Human total size of the staged files ("95,7 MB totali"). */
function fmtTotalSize(staged: StagedFile[]): string {
  const bytes = staged.reduce((sum, s) => sum + s.file.size, 0);
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1).replace(".", ",")} MB totali`;
  if (bytes >= 1000) return `${Math.round(bytes / 1000)} KB totali`;
  return `${bytes} B totali`;
}

function fmtElapsed(totalSecs: number): string {
  const h = Math.floor(totalSecs / 3600);
  const m = Math.floor((totalSecs % 3600) / 60);
  const s = totalSecs % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${String(s).padStart(2, "0")}s`;
  return `${s}s`;
}

type Tone = "ok" | "warn" | "err" | "info";

const TONE: Record<Tone, { fg: string; bg: string }> = {
  ok: { fg: "var(--c-ok)", bg: "var(--c-ok-soft)" },
  warn: { fg: "var(--c-warn)", bg: "var(--c-warn-soft)" },
  err: { fg: "var(--c-err)", bg: "var(--c-err-soft)" },
  info: { fg: "var(--c-ink-2)", bg: "var(--c-bg-elev)" },
};

/**
 * What the finished run actually concluded — not merely that it finished.
 *
 * `completed` is not the same fact as `trustworthy`: TORINO Phase 2 completed
 * green while reporting € 6.782.691,41 against the computo's own € 3.841.565,84.
 * Whoever reads this decides whether to believe the numbers, so it reads the
 * verdicts rather than the status. **Absence must never read as a pass** — a run
 * with no verdict says so, in words.
 *
 * > **Scope narrowed 2026-08-19 by `P-32`, and the sentence above was edited to
 * > match rather than left standing.** It used to open "This screen is where
 * > someone decides…", and that is no longer this screen: `completionDisplay`
 * > below gates this function out of the intake card, which now reports only
 * > that the run finished. The rule still governs **this function** and the two
 * > **Audit banners**, which is where the decision is now made and where every
 * > branch below is still reachable. What the intake card does instead — and
 * > what that costs — is stated on `completionDisplay`.
 */
export function completionVerdict(job: AnalysisJob): { tone: Tone; title: string; body: string } {
  const rec = reconciliationOf(job);
  const cat = categoryReconciliationOf(job);

  if (rec?.status === "over_extracted") {
    return {
      tone: "warn",
      title: "Analisi completata, ma i totali non quadrano",
      body: "L'estratto contiene più denaro di quanto il computo dichiari. Verifica i totali prima di usarli.",
    };
  }
  // Severity keys on `residual_explained`, never on `explanation !== null`: the
  // backend returns prose on both branches, including "this matches nothing,
  // rows were probably lost".
  if (rec?.status === "under_extracted" && rec.residual_explained !== true) {
    return {
      tone: "warn",
      title: "Analisi completata, ma manca del denaro rispetto al computo",
      body: "L'estratto contiene meno denaro di quanto il computo dichiari, e la differenza non è spiegata da una voce del documento. Verifica prima di usare i totali.",
    };
  }
  if (cat?.status === "mismatch") {
    return {
      tone: "warn",
      title: "Analisi completata, ma la ripartizione per categoria non coincide",
      // The old wording opened "Il totale quadra, ma…" — asserted from a branch
      // that sits ABOVE the grand-total check and never reads `rec`. With no
      // reconciliation verdict at all it stated, as fact, something nothing had
      // measured. Only claim it where it was actually established.
      body:
        rec?.status === "ok"
          ? "Il totale generale quadra, ma alcune categorie no. Verificale prima di usarle."
          : "Alcune categorie non coincidono con quelle dichiarate dal computo. Verificale prima di usarle.",
    };
  }
  // ── Beyond this point nothing DISAGREED. What is left is how much was
  //    actually verified, and the category axis qualifies every one of them.
  //
  //    `mismatch` is the only category status that is a disagreement (handled
  //    above); `partial`, `axis_not_extracted`, `not_comparable` and `unreadable`
  //    all mean the check did not complete. Computed ONCE here rather than per
  //    branch — the previous shape tested it inside the `rec === "ok"` branch
  //    only, so the branch added next to it inherited a clean tone while never
  //    reading `cat` at all. Absence reading as a pass, reintroduced one branch
  //    below the place it had just been fixed.
  const categoriesUnverified = !!cat && cat.status !== "ok";
  const categoryCaveat = categoriesUnverified
    ? " Il controllo per singola categoria non è stato completato, quindi non conferma nulla in un senso o nell'altro."
    : "";

  if (rec?.status === "ok") {
    return {
      tone: categoriesUnverified ? "info" : "ok",
      title: categoriesUnverified
        ? "Analisi completata — i totali quadrano, le categorie non sono state verificate"
        : "Analisi completata",
      body:
        "Abbiamo estratto le voci del computo e confrontato i prezzi. I totali quadrano con quelli dichiarati dal computo." +
        categoryCaveat,
    };
  }

  // A shortfall the document itself ACCOUNTS FOR is a check that RAN, not one
  // that is missing — folding it into "controllo non disponibile" reported the
  // best result short of `ok` as though nothing had been measured.
  //
  // But it is NOT a clean pass, and this is deliberately `info` rather than
  // `ok`. Two reasons, both measured. `residual_explained` guarantees only that
  // ONE stated line falls within ~1 cent per row of the residual — there is no
  // magnitude bound, so the identical verdict is returned when the unexplained
  // share is 97% of the project. And `ReconciliationBanner` renders this same
  // verdict amber, with the figures; a green tone here would have put two
  // surfaces of the same product in visible disagreement about the same money.
  //
  // NOTE 2026-08-19: that last clause still governs THIS function, and the
  // `info` below is still what the Audit page agrees with. It no longer
  // describes the intake card, which Omar ruled green for every completed run
  // (`P-32`) knowing it disagrees with an `err` banner. Read as a constraint on
  // `completionVerdict`, not as a claim about the product's two surfaces.
  // The wording keeps the backend's own hedge ("verosimilmente") instead of the
  // flat "Non ci sono righe perse" it does not support.
  if (rec?.status === "under_extracted" && rec.residual_explained === true) {
    return {
      tone: "info",
      title: "Analisi completata — la differenza rispetto al computo è spiegata da una voce",
      body:
        "L'estratto contiene meno denaro di quanto il computo dichiari, e la differenza corrisponde a una voce che il documento stesso indica — verosimilmente un importo non dettagliato, non righe perse. Apri l'audit per vedere gli importi." +
        categoryCaveat,
    };
  }

  return {
    tone: "info",
    title: "Analisi completata — controllo dei totali non disponibile",
    body:
      "Questa analisi non ha prodotto un esito sul controllo dei totali. Aprila nell'audit per verificarla." +
      categoryCaveat,
  };
}

/**
 * What the completion card actually PRINTS — `P-32`, Omar, 2026-08-19.
 *
 * `completionVerdict` above is untouched and still computes the full branch
 * table; this is the gate in front of it. By default the card says only that
 * the run finished, and sends the reader to the Audit page, where both
 * reconciliation banners still carry every figure. Pass `showVerdict` to get
 * the verdict back — that leg is what makes "no warning is displayed" a
 * provable claim rather than an untested silence.
 *
 * The parameter is optional, and deliberately so: the memory
 * `feedback_an_optional_guard_is_one_you_will_forget` warns against optional
 * SAFETY predicates, because forgetting one lands you on the unsafe branch.
 * Here the default IS the ratified decision and the argument only ever opens
 * the display back up, so forgetting it cannot leak a verdict onto the screen.
 *
 * Only ever reached for a job that COMPLETED — every call site handles
 * `analysisFailed` on its own branch, which keeps saying so in red.
 */
export function completionDisplay(
  job: AnalysisJob,
  showVerdict: boolean = SHOW_RECONCILIATION_ON_COMPLETION,
): { tone: Tone; title: string; body: string } {
  if (showVerdict) return completionVerdict(job);
  return {
    /*
     * GREEN, by Omar's explicit decision of 2026-08-19 — and it is a decision
     * with a named cost, not an oversight, so the cost is written here.
     *
     * `ok` is this file's SUCCESS token (`TONE.ok.fg = var(--c-ok)`), and
     * `completionVerdict` emits it from exactly one branch: the one reached
     * when the grand total reconciles AND the category check agreed — under
     * this same title. So a completed run now looks identical, to a reader,
     * whether or not its totals were ever checked. The adversarial pass raised
     * that: 1 of 8 verdict shapes is green when the verdict is shown, 8 of 8
     * are green here, and the Audit banner for the worst of them is `err`. This
     * screen was briefly `info` for exactly that reason.
     *
     * Omar was shown the measurement and chose green anyway, which is his call
     * to make: the product's premise is that the numbers are right, and a
     * hedged completion screen sells doubt on every run including the ones that
     * are fine. What the decision costs is that **the completion card can no
     * longer distinguish a verified total from an unchecked one** — the whole
     * signal now lives on the Audit page, which the card's own button opens.
     *
     * The `✓` is deliberately NOT part of this: see `JobsWatcher.completionToast`.
     * Recorded: [[2026-08-19-p32-verdict-display]] §2a.
     */
    tone: "ok",
    title: "Analisi completata",
    // Deliberately claims nothing about the numbers in either direction — not
    // "i totali quadrano" (which this branch has not read) and not a caveat
    // (which is the whole point of P-32). It states what happened and points at
    // the page that answers the question, because hiding the verdict here is
    // only defensible while the route to the banners stays one click away.
    body: "L'analisi del computo è terminata. Apri l'audit del progetto per rivedere le voci e i prezzi.",
  };
}

interface UploadViewProps {
  onBack: () => void;
  /**
   * `P-63` — an EXISTING project, reopened from `/projects/:projectId/prepare`.
   *
   * Absent means the other caller: `/`, creating something new. The two differ
   * in exactly one thing, and it is what this whole item is about — where
   * `project` comes from. Created here, or read back from the server.
   *
   * Passed in ALREADY RESOLVED rather than fetched here, and that is a
   * deliberate design decision with a measurement behind it. Fetching it here
   * meant two more `useQuery` calls inside this component; every one of this
   * screen's ~54 test files mocks `lib/tenderApi` with `{...actual, <the hooks
   * it needs>}`, so any hook NOT named in that list runs for real and demands a
   * QueryClient the harnesses do not provide. Measured: **390 of 1443 tests
   * failed, 767 of them on «No QueryClient set»**, none of it a defect in the
   * feature. `IngestPage` owns the route, so it resolves the route's parameter
   * and hands down a value — which is also the better shape, and the cheaper
   * one is not always the worse one.
   */
  reopenedProject?: TenderProject;
  /**
   * `P-63` — the analysis already running (or finished) for a reopened project.
   *
   * Resolved by `IngestPage` from `/v1/projects/{id}/jobs/latest`, which
   * defaults to `kind=analyze` (`api/jobs.py:234`), so an archive IMPORT can
   * never be mistaken for one. Without it a reopened project with a live run
   * renders `awaitingStart` — a wizard offering «Avvia» over a paid job already
   * in flight.
   */
  reopenedJobId?: string | null;
}

export function UploadView({
  onBack,
  reopenedProject,
  reopenedJobId,
}: UploadViewProps): JSX.Element {
  const navigate = useNavigate();
  const [drag, setDrag] = useState(false);
  /* `P-37`/A8 — the "Cosa succede dopo il caricamento" disclosure. Closed by
     default, exactly as the prototype has it. */
  const [explainerOpen, setExplainerOpen] = useState(false);
  const [name, setName] = useState("");
  const [staged, setStaged] = useState<StagedFile[]>([]);
  /**
   * Percent of each file's bytes that have left the machine, by staged key
   * (`P-41`). Written by the XHR transport's progress events during `submit()`;
   * `100` means the body is fully sent and the server is still working — for a
   * ZIP that window is the whole "apertura e riconoscimento" phase, and it is
   * the state the old static label hid. Absent key = no progress event yet.
   * Reset at the top of every submit pass, so a retry starts from an empty bar
   * rather than the previous attempt's 100%.
   */
  const [uploadPct, setUploadPct] = useState<Record<string, number>>({});
  /**
   * `P-48` — how far the SERVER has got importing an archive, per staged row.
   *
   * Three distinguishable states, and the distinction is the point:
   *   key absent  — no ZIP accepted yet; the row is still uploading bytes
   *   value null  — accepted, but the job has not published a count yet
   *   value       — «imported of total», as the server counts DOCUMENTS
   *
   * Collapsing the middle state into `{imported: 0, total: 0}` would print
   * «0 di 0 documenti», which is a claim about the bundle rather than about our
   * own ignorance. Reset with `uploadPct` at the top of every submit pass.
   */
  const [importCounts, setImportCounts] = useState<
    Record<string, { imported: number; total: number } | null>
  >({});
  const [error, setError] = useState<string | null>(null);
  /**
   * How many documents intake proposed, as counted at upload time.
   *
   * Only a FALLBACK for the window before the server's list arrives — the note
   * itself is derived from `documents` whenever there are any. Storing the
   * rendered sentence in state instead meant a monotonic count written once
   * inside `submit()`, which went on demanding an approval for a document that
   * had since been approved and forwarded, beside a card correctly reporting it
   * as sent.
   */
  const [proposedCount, setProposedCount] = useState(0);
  const [missingRoles, setMissingRoles] = useState<TenderDocRole[]>([]);
  /** Goal 55B, D16 — the server's refusal to start over the user's work on the saved result:
   *  what would be lost, the token confirming exactly that, and which start it refused. */
  const [workLoss, setWorkLoss] = useState<(UserWorkRefusal & { withKb: boolean }) | null>(null);
  // Which documents intake has PROPOSED for the Knowledge Base, accumulated
  // across every attempt in this flow.
  //
  // A ref and a Set, both deliberately. `submit()` rebuilds its `created` list
  // per call and skips files already uploaded, so after a partial failure the
  // retry pass sees only the file it re-sent — counting that alone reported
  // "1 documento proposto" where seventeen more had been proposed on the first
  // pass. Found by the independent design pass, 2026-08-12. The Set is keyed on
  // document id so a retry cannot double-count one that already landed.
  const proposedRef = useRef<Set<string>>(new Set());
  // The created project survives retries — a partial upload failure must never
  // create a duplicate project on the second attempt.
  const [createdProject, setCreatedProject] = useState<TenderProject | null>(null);
  /**
   * `P-63` — reopening supplies the project; creating remembers the one it made.
   *
   * DERIVED, never an effect copying the prop into state. An effect would give
   * this screen two sources for one fact and a window where they disagree — and
   * the window is the dangerous one: `submit()` creates a project when
   * `project` is falsy, so a reopened screen whose copy had not landed yet
   * would create a SECOND project for a bundle that already exists.
   */
  const project: TenderProject | null = reopenedProject ?? createdProject;
  /**
   * `P-63` round 2 — the project name is required to CREATE a project, and for
   * nothing else. `submit()` reads it in exactly two places: this guard, and
   * `createProject({ name })` inside `if (!proj)`. Once a project exists the
   * value is inert, which is why the field beside it is `disabled={!!project}`
   * and says «Il nome non si modifica da questa schermata».
   *
   * Writing the requirement as `!name.trim()` conflated "we need a name" with
   * "we are creating something". That held while the ONLY way to reach this
   * screen with a project was to have typed the name yourself moments earlier
   * — true for every path that existed before this branch. The reopen route
   * arrives with `project` set and `name` still `""`, so the commit button was
   * permanently `disabled`, with no message, on a lane that renders whenever
   * `stagedPending > 0`: a person could stage an errata PDF, see «Carica
   * documenti», press it and get nothing. Both gates below are byte-identical
   * to `origin/qa`; only the entry point is new.
   *
   * ONE predicate rather than the same expression at two call sites — the
   * button and `submit()` must not be able to disagree about when a name is
   * needed (`feedback_one_predicate_not_two_callers`).
   */
  const nameRequired = !project;
  /**
   * `P-63` — the run this screen is watching.
   *
   * `startedJobId` is what THIS session started; `reopenedJobId` is what the
   * server says was already running or finished. Session first, so pressing
   * Avvia latches on immediately instead of waiting for the next poll.
   */
  const [startedJobId, setStartedJobId] = useState<string | null>(null);
  const jobId: string | null = startedJobId ?? reopenedJobId ?? null;
  /**
   * The same fact, readable from inside an async function that started earlier.
   *
   * `submit()` closes over the `jobId` of the render that invoked it. QA found
   * that in the window where a run could begin DURING an upload, the closure
   * still held `null`, so the live region announced "Controllali e avvia
   * l'analisi" beside a card already reading "In corso" — inviting a second
   * start of something already running. Closing that window (see the Avvia
   * guard) removes the only route to it; this removes the class.
   */
  const jobIdRef = useRef<string | null>(null);
  const [busy, setBusy] = useState(false);
  /**
   * Which of the two confirmation steps is on screen, before the run starts.
   *
   * Only meaningful while `awaitingStart` — once a job exists the wizard is
   * over and the screen becomes the progress surface. Deliberately NOT reset
   * anywhere: going back to Passo 1 and forward again must not lose the KB
   * ticks, which live in `useKbDecision` and are keyed on document id.
   */
  const [step, setStep] = useState<1 | 2>(1);
  /**
   * `P-37`/B1+C1 — which PANEL the Fase 2/3 card is showing.
   *
   * Deliberately NOT a second copy of `step`. While the run is still waiting to
   * be started, `step` is the authority and this is ignored; from the moment a
   * job exists, `step` stops meaning anything (there is nothing left to confirm)
   * and this takes over, with a third value `step` cannot hold. Two states that
   * were both live at once would be two places to forget to update — and the
   * one that gets forgotten is always the one that only renders after a paid
   * run has started.
   *
   * Defaults to "3": the moment you press Avvia, the thing you want to see is
   * the run, not the list you just confirmed.
   */
  const [viewTab, setViewTab] = useState<"1" | "2" | "3">("3");
  /**
   * Intake has committed at least one document — a strictly earlier signal than
   * the documents query returning.
   *
   * It exists because removing the auto-start removed the old non-racy signal.
   * `phase` used to reach 2 via `jobId`, which `beginAnalysis` set inside
   * `submit()`; with nothing starting on upload, the only remaining trigger was
   * `documents.length > 0` — a react-query read `submit()` does not await. That
   * left the screen on "Fase 1 di 3 — carica e riconosci", with a live commit
   * button, for the whole window after a successful upload. Set from the
   * upload's OWN response, which is the fact rather than a cached view of it.
   */
  const [uploadCommitted, setUploadCommitted] = useState(false);
  /**
   * A SYNCHRONOUS latch on the start, not `startAnalysis.isPending`.
   *
   * The button is also disabled on `isPending`, and that is the visible half.
   * This is the half that works: React state is asynchronous, so two clicks
   * landing in the same tick both read `isPending === false` and both call
   * through. `useKbDecision` already carries exactly this ref for exactly this
   * reason — its second call was a second Knowledge Base approval — and Avvia
   * is the more expensive button of the two.
   *
   * The backend dedups one active run per project, so the worst case was never
   * two paid runs; it was a second `kb.submit()` and a confusing screen. Guard
   * anyway: "something downstream would have caught it" is not a guard.
   */
  const startingRef = useRef(false);
  /**
   * `P-79` — `startRun` announces a REMEDY, and it runs long before
   * `hideWizard` is computed further down this render. A ref keeps the spoken
   * announcement and the visible notice naming the SAME place instead of
   * giving the announcement its own copy of the rule — the failure this file
   * has already paid for twice, where two pointers for one remedy made a
   * reader wonder whether they were two remedies.
   */
  const hideWizardRef = useRef(false);
  /** What the live region announces. Set at each phase transition. */
  const [announcement, setAnnouncement] = useState("");
  /**
   * Names of documents the LAST pass uploaded, for the phase-2 receipt.
   *
   * From phase 2 an uploaded row leaves the staging list (it belongs to the
   * review panel below, with its real type). But `submit()` does not await the
   * refetch that puts it there, so for that window the file was in neither list
   * and the user had no confirmation at all that a mid-analysis upload worked.
   *
   * A receipt rather than keeping the row: matching a staged row against the
   * server list by FILENAME cannot work for an archive — the staged row is
   * `bundle.zip` and no document is ever called that — so the row would have sat
   * there forever on exactly the input this screen is built around.
   */
  const [justUploaded, setJustUploaded] = useState<string[]>([]);
  /** `P-45` — one sentence per ZIP that imported SOME of its files (Omar,
   *  2026-08-24, "Option A"). Separate from the receipt above: the receipt is
   *  deliberately short-lived and dies the moment the documents it promises
   *  appear, whereas this is the thing the user is being asked to check
   *  against that very list, so it must outlive it. */
  const [partialZips, setPartialZips] = useState<string[]>([]);
  /** The same facts, keyed by staged-file key, surviving ACROSS passes.
   *
   *  A retry re-sends only the files that failed (`s.state === "uploaded"`
   *  continues), so a ZIP that came in short is never re-examined — and a plain
   *  per-pass rebuild therefore DELETED its notice on the retry that fixed an
   *  unrelated sibling. That is the worst possible moment to lose it: the user
   *  has just cleared the error and is about to start the analysis.
   *
   *  Keyed rather than appended so a corrected re-upload REPLACES its own line,
   *  and a ZIP that comes back complete deletes it. */
  const partialRef = useRef<Map<string, string>>(new Map());
  /** Document count when the receipt was written — it expires once the list grows. */
  const [receiptBaseline, setReceiptBaseline] = useState(0);

  const createProject = useCreateProject();
  const uploadDocument = useUploadDocument();
  const uploadArchive = useUploadArchive();
  /* `P-48` round 4 — is an import ALREADY running for this organisation?
     Org-wide rather than project-scoped, because after a reload this screen has
     no project: it mounts at `/` with no id in the URL and holds `project` in
     plain `useState`. Without this the page rendered a blank «Nuovo progetto»
     over a live import, and a user who reasonably re-dropped the ZIP got a
     SECOND project importing a second copy — the per-project 409 cannot fire
     when the two uploads create two different projects. */
  const activeImport = useActiveImportJob().data ?? null;
  /* Ours, or somebody else's? While `submit()` is running WE are the importer
     and the row already shows the count, so the banner would be a duplicate.
     `busy` is the honest discriminator: it is true for exactly the pass this
     component is driving. */
  const foreignImport = !busy && activeImport ? activeImport : null;
  const foreignImportCounts = archiveImportCounts(foreignImport);
  const startAnalysis = useStartAnalysis();
  const job = useAnalysisJob(jobId).data;
  const isKbAdmin = useCanApproveKb();
  /* `KB-22` — the platform operator, not a customer admin.

     Omar chose `super_admin` over a QA mode or an environment flag, and the
     reason is that there IS no environment flag anywhere in this product:
     neither backend's `Settings` has one and `import.meta.env.MODE`/`.DEV`/
     `.PROD` are used zero times in `frontend/src`, so one would be a new concept
     across three services and three environments, live wherever someone set it.
     `super_admin` is already "held by nobody inside a customer organization".

     `useCapability("platform:manage")` and NOT `useIsSuperAdmin()`, for two
     reasons and the second one is the real one.

     (1) `platform:manage` is granted to `SUPER_ADMIN` alone — server
     `capabilities.py:123`, browser mirror `kbApi.ts:340`, and `admin` does not
     carry it — so it means exactly "the vendor", read through the table whose
     own rule is that it may only ever be a SUBSET of the server's. A raw role
     claim answers a different question, one nothing enforces.

     (2) Measured, not guessed: adding `useIsSuperAdmin` here turned **407 of
     1503 tests red across 54 files**, all with `No "useIsSuperAdmin" export is
     defined on the "../lib/kbApi" mock`. Those files mock the module with an
     explicit object, so ANY new export breaks them — the same trap `P-63` hit
     with `useProject`. `useCapability` is already in every one of those mocks as
     `(c) => caps.granted.has(c)`, so this reads the same fact with zero test
     churn and lets each test grant or deny it through the mechanism it already
     has. A design that needs 54 files edited to make it pass is a design worth
     rechecking before editing 54 files. */
  const isPlatformAdmin = useCapability("platform:manage");
  /**
   * `ACC-11`. Not a prediction about documents — a fact about the caller, read
   * from the access token, mirroring `capabilities.ROLE_CAPABILITIES`. A
   * Reviewer holds nothing and must not be offered a 1-3 hour paid run; the
   * server refuses it too (`route_capabilities.py`: POST .../analyze →
   * analysis:run), so this only saves them a 403.
   *
   * Deliberately NOT the KB gate. The prototype disabled its single button on
   * `nonAdmin`, which was not a role decision but a side effect of merging two
   * controls: the Knowledge Base is admin-only because it spends money, and
   * bundling the analysis into the same button silently extended that gate to
   * the analysis — locking an ordinary estimator, the primary user of this
   * screen, out of the product's main action.
   */
  const canRunAnalysis = useCapability("analysis:run");

  // The list is read from the server, never accumulated from upload responses:
  // every mutation that can change it (upload, archive, kind/role correction,
  // KB decision) already invalidates this query, so the panel self-heals after
  // each correction instead of needing a parallel patching layer.
  const documentsQuery = useProjectDocuments(project?.id ?? null);
  const documents: TenderDocument[] = documentsQuery.data ?? [];
  const kb = useKbDecision(documents, project?.id ?? null);
  /**
   * `L4-07` — the Knowledge Base lane's percentage.
   *
   * Read from the KNOWLEDGE BASE, not from `kbForwardState`. `forwarded` on
   * this side means the KB answered 2xx — and it answers **202 Accepted**,
   * because its ingest is an async job. A bar built from the states this file
   * already holds would reach 100% the instant the last file was handed over,
   * with the synthesis that makes documents usable still running.
   *
   * The denominator is what the user APPROVED, not what the KB already holds:
   * a document said yes to but not yet forwarded is real work not yet done, and
   * counting only what arrived would show 100% with one of ten files in.
   */
  const kbQuery = useKbProjectDocuments(project?.id ?? null);
  /* The approved DOCUMENTS, not a count. Option B follows each one by the id
     the Knowledge Base gave it, so the list itself has to reach the
     derivation — a number cannot say WHICH. */
  const kbApproved = documents.filter((d) =>
    ["queued", "forwarding", "forwarded"].includes(d.kbForwardState),
  );
  /* `isSuccess`, never `.data ?? []`. An errored query has `data ===
     undefined` exactly like a pending one, so the old form printed a
     confident 0% over a walk that had FAILED. Requiring success covers
     loading, error and truncation at once — and any state added later,
     because nothing here has to remember them. */
  const kbProgress = kbQuery.isSuccess
    ? kbSynthesisProgress(kbApproved, kbQuery.data)
    : KB_PROGRESS_UNAVAILABLE;
  /**
   * The review panel's «Da confermare» filter, owned HERE.
   *
   * It is a display filter, so the panel is the obvious home — and that is
   * exactly where it was, and why the first fix for this failed. On TORINO the
   * flagged documents and the KB-eligible ones are disjoint sets, so one click
   * empties the Knowledge Base group; the panel guarded its own commit bar, but
   * in the wizard that bar does not exist and the button that spends the money
   * is the sticky Avvia below — which could not see a flag held one component
   * down. State that is a precondition of a commit belongs where the commit is.
   */
  const [onlyReview, setOnlyReview] = useState(false);
  /**
   * Owned here, NOT inside the review panel — the same medicine `kb` is already
   * on, for the same reason.
   *
   * `P-37` made the panel a conditional child of the tab strip, and the hook
   * lived inside it. So a type correction refused by the server while the reader
   * moved to Avanzamento lost its only surface: React 18 makes `setError` on an
   * unmounted component a silent no-op, the PATCH is not aborted (our client
   * passes an abort signal for queries and never for mutations), and nothing
   * gates Avvia on a correction being in flight. On remount the per-field lock
   * is a fresh empty Set, so the select re-enables showing the OLD value with no
   * alarm anywhere — and the panel's own header invites correcting a type
   * mid-run, so this is the ordinary path.
   */
  const correction = useDocumentCorrection(project?.id ?? null);
  // Owned here for the same reason `correction` is: `P-37` made the review
  // panel a conditional child, so a hook living inside it loses its in-flight
  // state the moment the customer changes tab.
  const download = useDocumentDownload();
  // `P-55` — a SECOND downloader, not a reuse of the one above. Both mint a
  // short-lived URL and save it, but from different endpoints and over
  // different id spaces, and the busy/error state is keyed by id: sharing one
  // would let a price-list workbook's in-flight download disable a document row
  // that happened to carry the same uuid. Owned here for the same reason
  // `download` is — the review panel is a conditional child.
  const priceListDownload = usePriceListFileDownload(project?.id ?? null);

  /**
   * `P-43` — the missing-required row's direct upload, owned here because the
   * project id, the upload mutation and its invalidations all live here.
   *
   * One upload at a time by design, and the latch is a SYNCHRONOUS ref beside
   * the state: a drop event is not disabled by a re-render, so between a second
   * drop and the state update landing there is a window `useState` cannot
   * close — the same shape as `startingRef` above. The row disappears when the
   * refetched document list carries the role — the server's answer, never a
   * client-side prediction.
   */
  const missingUploadRef = useRef(false);
  const [missingUploadState, setMissingUploadState] = useState<{
    role: TenderDocRole;
    name: string;
    pct: number | null;
  } | null>(null);
  const [missingUploadError, setMissingUploadError] = useState<{
    role: TenderDocRole;
    message: string;
  } | null>(null);
  function startMissingUpload(role: TenderDocRole, file: File): void {
    if (!project || missingUploadRef.current) return;
    if (isArchive(file)) {
      /* A ZIP dropped on a single-document slot would be stored AS the missing
         document — role preassigned, recogniser overruled — and the analysis
         would then run against an archive. Refuse it with the remedy named. */
      setMissingUploadError({
        role,
        message: "Qui serve il singolo documento, non uno ZIP: trascina il file giusto.",
      });
      return;
    }
    missingUploadRef.current = true;
    setMissingUploadError(null);
    setMissingUploadState({ role, name: file.name, pct: null });
    void (async () => {
      try {
        await uploadDocument.mutateAsync({
          projectId: project.id,
          file,
          role,
          onProgress: (loaded, total) => {
            if (total <= 0) return;
            const pct = Math.min(100, Math.round((loaded / total) * 100));
            setMissingUploadState((prev) => (prev ? { ...prev, pct } : prev));
          },
        });
        // The 422 notice's list is stale the moment a file lands — same rule
        // as `addFiles`. The server re-answers on the next Avvia.
        setMissingRoles([]);
      } catch (e) {
        setMissingUploadError({ role, message: apiErrorMessage(e) });
      } finally {
        missingUploadRef.current = false;
        setMissingUploadState(null);
      }
    })();
  }

  /**
   * `P-44` — the price-list card's data and actions, owned here for the same
   * reason as `missingUpload`: project id, mutations and invalidations live
   * here, and the panel only renders. An uploaded listino is an ordinary
   * document with role='regional'; a library association has its own endpoint.
   */
  const priceListsQuery = useProjectPriceLists(project?.id ?? null);
  const removePriceList = useRemoveProjectPriceList();
  // `P-47b` — the library the picker offers. Asked for only once a project
  // exists, like its sibling above.
  const priceListLibraryQuery = usePriceListLibrary(project?.id ?? null);
  const addPriceList = useAddProjectPriceList();
  const addingRef = useRef<Set<string>>(new Set());

  /**
   * `P-47` — does this project have a price book?
   *
   * The SAME question the server answers at `POST /analyze`, asked here only so
   * the user is stopped at the cheap door instead of the expensive one. Derived
   * from exactly the two sources the card renders, so the button and the card
   * cannot disagree: a non-archived uploaded `regional` document, or a live
   * library association whose edition owns at least one FILE. Counting
   * associations instead of files would pass a project that stages no workbook
   * — the same hole the server-side predicate had to close.
   *
   * FAILS OPEN, deliberately. While the answer is unknown — the query pending
   * or errored — the button stays enabled and the server refuses with the
   * structured 422 that names the missing role. A client-side prediction that
   * blocks on its own uncertainty strands the user on a transient network
   * error, and this project's record is that a prediction kept in agreement
   * with reality forever is the thing that keeps breaking; the authority is one
   * round trip away and it answers 4xx.
   */
  const hasPriceBook =
    documents.some((d) => d.role === "regional" && d.status !== "archived") ||
    (priceListsQuery.data ?? []).some((e) => e.files.length > 0);
  // BOTH queries, not one. `hasPriceBook` reads two independent sources, and
  // this line used to gate on the success of only ONE of them while the comment
  // above claimed the whole thing fails open. `documents` is `.data ?? []`, so
  // an unloaded or ERRORED documents query was indistinguishable from "this
  // project has no uploaded listino".
  //
  // Reachable without any error at all: an archive import invalidates both
  // caches in the same `onSuccess`, and they refetch concurrently. The
  // price-list response is a handful of rows; the documents response carries
  // every title block. If price-lists lands first, then for the width of one
  // round trip the screen disables «Continua al passo 2» and prints «Manca il
  // listino prezzi di riferimento» over a project whose uploaded listino is in
  // the response still in flight. Permanent if that fetch errors on first load.
  //
  // Found by two independent lenses of the adversarial review, which is the
  // strongest signal in a set of findings.
  const missingPriceBook = priceListsQuery.isSuccess && documentsQuery.isSuccess && !hasPriceBook;
  // A SYNCHRONOUS latch, not `disabled`. `removeBusy` only becomes true on the
  // re-render after the first click, so a double-click sent two DELETEs and the
  // second came back 404 — surfacing the server's raw English «price list not
  // associated» in an alert, for an action that had in fact succeeded. Cleared
  // when the mutation settles, so a real retry still works.
  const removingRef = useRef<Set<string>>(new Set());
  const deleteDocument = useDeleteDocument();
  // `D-41`. This panel's «Rimuovi» calls the SAME route the project screen does,
  // and that route is now administrator-only. Without this the control would
  // still be offered to a member and would fail with a 403 they never asked for
  // — a refusal is only a permission rule if the person can tell it apart from a
  // fault.
  const canDeleteDocument = useCanDeleteDocument();
  const [refusedDocDelete, setRefusedDocDelete] = useState<string | null>(null);
  const listinoUploadRef = useRef(false);
  const [listinoUpload, setListinoUpload] = useState<{ name: string; pct: number | null } | null>(
    null,
  );
  const [listinoError, setListinoError] = useState<string | null>(null);
  function startListinoUpload(file: File): void {
    if (!project || listinoUploadRef.current) return;
    if (isArchive(file)) {
      setListinoError("Qui serve il singolo listino (.xlsx), non uno ZIP.");
      return;
    }
    listinoUploadRef.current = true;
    setListinoError(null);
    setListinoUpload({ name: file.name, pct: null });
    void (async () => {
      try {
        await uploadDocument.mutateAsync({
          projectId: project.id,
          file,
          role: "regional",
          onProgress: (loaded, total) => {
            if (total <= 0) return;
            const pct = Math.min(100, Math.round((loaded / total) * 100));
            setListinoUpload((prev) => (prev ? { ...prev, pct } : prev));
          },
        });
      } catch (e) {
        setListinoError(apiErrorMessage(e));
      } finally {
        listinoUploadRef.current = false;
        setListinoUpload(null);
      }
    })();
  }

  /* `P-63` — `jobIdRef` exists so async closures can read the CURRENT job id
     rather than the one their render captured. `beginAnalysis` sets it
     synchronously for a run this session starts; a REOPENED run never goes
     through `beginAnalysis`, so without this the ref stayed null over a live
     analysis and `submit()` announced "controllali e avvia l'analisi" beside a
     job already running — the exact sentence its own guard at :1456 exists to
     prevent. Assigning the same value twice on the start path is harmless. */
  useEffect(() => {
    jobIdRef.current = jobId;
  }, [jobId]);

  const analyzing = !!jobId && (!job || job.status === "queued" || job.status === "running");
  const analysisFailed = job?.status === "failed";
  const analysisDone = job?.status === "completed";
  /**
   * Only an in-flight upload freezes staging — NOT a running analysis.
   *
   * It used to include `analyzing || analysisDone`, which was survivable while
   * the staging list and its commit button lived on the same screen as the drop
   * zone. Once they became phase-1-only, the drop zone stayed live in phase 2
   * with nothing to send the files: a dropped file entered `staged` state and
   * became both invisible and unsendable. Adding a document mid-run is a real
   * thing to do — public tenders publish errata after the bundle — so the fix is
   * that staging keeps working, not that the drop zone gets disabled.
   */
  const stagingLocked = busy;
  const classified = !!project && documents.length > 0;
  /**
   * `jobId` is part of this, and must be.
   *
   * Deriving the phase from the document list alone raced a refetch `submit()`
   * never awaits: between the analysis starting and the documents query
   * returning, the screen went on claiming "Fase 1 di 3 — carica e riconosci"
   * with a live commit button, while a paid 1–3 hour run was already going. A
   * started job is a strictly earlier and non-racy signal that we are past
   * intake.
   */
  const phase: 1 | 2 | 3 =
    analysisDone || analysisFailed ? 3 : classified || jobId || uploadCommitted ? 2 : 1;
  /**
   * Documents are in, and nothing has been started yet — the confirm-then-start
   * wizard. `P-31`.
   */
  const awaitingStart = phase === 2 && !jobId;

  // Elapsed clock while the analysis runs.
  const startedAtRef = useRef<number | null>(null);
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!analyzing) return undefined;
    startedAtRef.current = startedAtRef.current ?? Date.now();
    const t = window.setInterval(() => {
      if (startedAtRef.current) {
        setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000));
      }
    }, 1000);
    return () => window.clearInterval(t);
  }, [analyzing]);

  useEffect(() => {
    if (analysisDone) setAnnouncement("Analisi completata.");
    else if (analysisFailed) setAnnouncement("Analisi non riuscita.");
  }, [analysisDone, analysisFailed]);

  /**
   * Stage files, keeping the folder each came from.
   *
   * Accepts the picker's plain `File[]` (folder position read from
   * `webkitRelativePath`) and the drop walker's already-resolved pairs, so both
   * routes in produce identical staging.
   */
  function addFiles(incoming: File[] | DroppedFile[]): void {
    if (stagingLocked) return;
    const resolved: DroppedFile[] = incoming.map((item) =>
      item instanceof File ? { file: item, folderPath: folderPathOf(item) } : item,
    );
    setStaged((prev) => {
      const seen = new Set(prev.map((s) => s.key));
      const fresh: StagedFile[] = [];
      for (const { file, folderPath } of resolved) {
        const key = stagedKey(folderPath, file.name);
        if (seen.has(key)) continue;
        seen.add(key); // also dedupes within one drop, not just against what is staged
        fresh.push({ key, file, folderPath, role: guessRole(file.name), state: "staged" });
      }
      return [...prev, ...fresh];
    });
    setMissingRoles([]);
  }

  function setRole(key: string, role: TenderDocRole): void {
    setStaged((prev) => prev.map((s) => (s.key === key ? { ...s, role } : s)));
    setMissingRoles([]);
  }

  function setFileState(key: string, state: FileState): void {
    setStaged((prev) => prev.map((s) => (s.key === key ? { ...s, state } : s)));
  }

  async function submit(): Promise<void> {
    setError(null);
    setMissingRoles([]);
    const trimmed = name.trim();
    if (nameRequired && !trimmed) {
      setError("Inserisci il nome del progetto.");
      return;
    }
    setBusy(true);
    setJustUploaded([]);
    setUploadPct({});
    setImportCounts({});
    try {
      // 1. Create the project exactly once (retries reuse it).
      let proj = project;
      if (!proj) {
        proj = await createProject.mutateAsync({
          name: trimmed,
          // `P-37`/A2 · `P-38`: no longer asked here. The API still accepts both
          // and the columns still exist — the analysis becomes responsible for
          // finding them, so this call deliberately sends neither rather than
          // sending an empty string that would look like an answer.
        });
        setCreatedProject(proj);
      }

      // 2. Upload every file not yet uploaded; keep going past failures so one
      //    bad file doesn't block the rest. A .zip is sent whole and expanded
      //    server-side — one request instead of thirty, and the folder tree
      //    inside it survives.
      let anyFailed = false;
      /* `P-43` took the archive-skip listing card with it, on Omar's order —
         but the order was about the always-on module, not about a bundle that
         imports NOTHING going quiet. An all-skipped ZIP otherwise reads
         «caricato ✓» over an empty project, which the record calls the one
         unacceptable outcome. This is the narrow failure notice, not the
         module returning. */
      let zipEmptyNotice: string | null = null;
      // Every document the server actually created, from BOTH paths. Needed to
      // count what intake proposed for the Knowledge Base: a ZIP is where most
      // narrative documents arrive, so counting only single uploads would report
      // zero on exactly the bundle that proposes eighteen.
      const created: TenderDocument[] = [];
      /** What we sent, by the name the user sees — the receipt's fallback. */
      const sentNames: string[] = [];
      for (const s of staged) {
        if (s.state === "uploaded") continue;
        setFileState(s.key, "uploading");
        /* `P-41` — the row's progress bar. `total > 0` guards the division; the
           transport already refuses to emit without a computable length, so a
           callback that fires carries real numbers. Clamped because a browser
           may report `loaded === total` more than once around the boundary. */
        const onProgress = (loaded: number, total: number): void => {
          if (total <= 0) return;
          const pct = Math.min(100, Math.round((loaded / total) * 100));
          setUploadPct((prev) => ({ ...prev, [s.key]: pct }));
        };
        try {
          if (isArchive(s.file)) {
            const result = await uploadArchive.mutateAsync({
              projectId: proj.id,
              file: s.file,
              onProgress,
              /* `P-48` — the server's own count, republished on every poll.
                 This is what replaces the full bar and the minute of silence:
                 the bytes have all arrived, and from here the honest subject is
                 documents, not bytes. */
              onImportProgress: (counts) =>
                setImportCounts((prev) => ({ ...prev, [s.key]: counts })),
            });
            if (result.documents.length === 0) {
              // The server answered 200 and imported nothing. Marking the row
              // failed is the honest state — and retry-safe, because an
              // all-skipped bundle created zero rows to duplicate.
              anyFailed = true;
              zipEmptyNotice = `Nessun documento è stato importato da ${s.file.name}: ${
                result.skipped.length
              } ${result.skipped.length === 1 ? "elemento non leggibile" : "elementi non leggibili"}${
                result.skipped[0]
                  ? ` (es. ${result.skipped[0].name}: ${result.skipped[0].reason})`
                  : ""
              }. Controlla il contenuto dello ZIP.`;
              setFileState(s.key, "failed");
              continue;
            }
            /* `P-45` — the bundle imported something, but not everything.
               Counted from `lostFiles`, which the SERVER derives from the same
               rows it sends: `skipped` also carries the ZIP's own directory
               records and `__MACOSX/` tree, and counting those would fire this
               notice on every real portal download (measured: 47 of 82 skipped
               members on TORINO Phase 3, every one of them bookkeeping, on a
               bundle that imported all 35 of its real documents).
               `null` means an older server did not say — and not knowing is not
               the same as knowing there were none, so we stay silent.

               `?? 0` rather than `!== null`: the first version of this guard
               tested only for `null` and let `undefined` through to `> 0`,
               which is false — so it happened to behave correctly while being
               wrong about why. It stayed green against every existing mock for
               exactly that reason, which is how it was found. One predicate
               covering both absences beats two spellings of "no answer". */
            const lost = result.lostFiles ?? 0;
            if (lost > 0) {
              partialRef.current.set(
                s.key,
                `Dallo ZIP ${s.file.name} sono stati importati ${result.documents.length} ` +
                  `file su ${result.documents.length + lost}.`,
              );
            } else {
              /* Keeps the map in step with the archive rather than only ever
                 growing. Be precise about what this does today: it is a NO-OP
                 in every sequence currently reachable. `stagedKey` is derived
                 from path + filename, so a corrected re-upload reuses the key —
                 but a ZIP that imported at all is marked `uploaded`, the retry
                 loop skips it, and its Remove button is `disabled`, so it is
                 never sent twice. This is here so the invariant holds by
                 construction if that ever changes, NOT because it prevents a
                 stale count today. */
              partialRef.current.delete(s.key);
            }
            created.push(...result.documents);
          } else {
            created.push(
              await uploadDocument.mutateAsync({
                projectId: proj.id,
                file: s.file,
                role: s.role,
                folderPath: s.folderPath || undefined,
                onProgress,
              }),
            );
          }
          sentNames.push(s.file.name);
          setFileState(s.key, "uploaded");
        } catch (err) {
          anyFailed = true;
          setFileState(s.key, "failed");
          /* `P-48` — when the SERVER wrote a sentence about what went wrong,
             show that sentence. Two cases reach here and both have one:

               · 409 «Un'importazione è già in corso per questo progetto.» The
                 generic fallback below says «Riprova: verranno reinviati solo
                 quelli», which is advice that cannot work — retrying is exactly
                 what fails again until the first import ends.
               · a failed import job, whose `error` is the runner's own message
                 and already names the remedy («ricarica lo ZIP per riprovare»).

             Only for the ARCHIVE branch: a single-file upload failing keeps the
             existing wording, which is right for it. Reuses `zipEmptyNotice`,
             which is already "the specific story beats the generic one" channel
             — one variable, one precedence rule, rather than a second one to
             keep in step with it. */
          if (isArchive(s.file) && zipEmptyNotice === null) {
            const detail = apiErrorMessage(err);
            if (detail) zipEmptyNotice = detail;
          }
        }
      }
      /* `P-43` (Omar, 2026-08-22) — the archive-skip module is GONE, and the
         removal is a recorded reversal of `P-31`'s carry-over, not an oversight:
         the server still reports `result.skipped`, and nothing here reads it any
         more. The «38 elementi dello ZIP non sono stati importati» card this fed
         was ordered off the screen with the prototype as the reference. */

      // Record what this pass proposed BEFORE the failure check below can return.
      // A document that uploaded successfully was proposed successfully, even if
      // a sibling file failed — and the retry will skip it, so this is the only
      // pass that will ever see it. Accumulating after the early return meant a
      // partial failure lost the entire pass's proposals, which is a worse
      // version of the undercount this fix was written for.
      for (const d of created) {
        if (d.kbForwardState === "pending") proposedRef.current.add(d.id);
      }
      setProposedCount(proposedRef.current.size);
      // Set BEFORE the `anyFailed` return below, for the same reason the KB
      // proposals are: a pass where one ZIP imported partially and a sibling
      // file failed outright has still learnt the partial fact, and the retry
      // re-sends only the failed file — so this is the only pass that will ever
      // see it.
      setPartialZips([...partialRef.current.values()]);
      // The receipt names what the SERVER created — for a ZIP that is its
      // members, not the archive the user picked. Gated on `created`, never on
      // what we sent: a ZIP whose members are ALL skipped resolves successfully,
      // so a fallback keyed on `sentNames` announced "1 documento caricato:
      // errata.zip" beside a live region saying "0 documenti aggiunti". The
      // fallback survives only for the case it was added for — a response that
      // omits `filename` — where at least one document really was created.
      if (created.length > 0) {
        const fromServer = created.map((d) => d.filename).filter(Boolean);
        setJustUploaded(fromServer.length > 0 ? fromServer : sentNames);
        setReceiptBaseline(documents.length);
        // Set here rather than after the `anyFailed` return below: a pass that
        // uploaded eighteen documents and failed on the nineteenth HAS committed
        // intake, and must not leave the screen on Fase 1 with the eighteen
        // invisible.
        setUploadCommitted(true);
      }

      if (anyFailed) {
        // The specific story beats the generic one: an unreadable bundle is not
        // fixed by pressing Riprova, and saying so is the difference between a
        // remedy and a loop.
        setError(
          zipEmptyNotice ??
            "Alcuni file non sono stati caricati. Riprova: verranno reinviati solo quelli.",
        );
        return;
      }

      // 3. The Knowledge Base is no longer written from here.
      //
      //    This used to POST the Relazione straight to the KB from the browser.
      //    KB-7 moved forwarding server-side, behind an Admin approval gate, and
      //    leaving this in place would have been a SECOND, ungated writer: the
      //    same document synthesised twice, and the gate walked around by doing
      //    the ordinary thing. Intake now marks every narrative document
      //    `pending` during upload; an Admin approves them below.
      //
      //    Nothing is lost by the removal — the server path sends `project_id`
      //    and `project_name` too, read from the project row rather than taken
      //    from the client, so attribution in the KB is if anything stronger.
      // 4. Auto-start the analysis when every required role is present.
      //    A ZIP contributes the roles the SERVER assigned to its members — the
      //    client only ever saw one file called something.zip, whose guessed
      //    role is "other", so judging by the staged list alone would report
      //    every required role missing on a bundle that in fact has them all.
      // 4. NOTHING starts here any more (`P-31`, Omar 2026-08-19). The upload
      //    ends at "we have read your documents"; the user confirms them and
      //    presses Avvia. `startRun` below is the only thing that spends money.
      //
      //    What follows is the reasoning that survived the reversal, because it
      //    still governs `startRun`: there is deliberately no client-side
      //    prediction of which required roles are present.
      //
      //    There used to be, and it broke three times running — each fix causing
      //    the next. A per-call role set made "add the missing file" report the
      //    ZIP's own documents as missing (dead end). A ref that accumulated
      //    across passes then remembered a role the project had LOST to a type
      //    correction, and fired the paid start beside a panel printing "Nuovi
      //    Prezzi — nessun documento riconosciuto". Pruning that ref against
      //    `documents` made it trust a react-query CACHE SNAPSHOT as truth about
      //    the server: one stale read and the start became unreachable, with no
      //    control left on screen to retry it.
      //
      //    Three breaks in one guard is the design failing, not the fixes. The
      //    server is the only thing that actually knows — it answers 422 with
      //    `detail.missing_roles`, which `missingRolesFromError` already parses
      //    in the catch below and which every one of those attempts was trying
      //    to predict. So: attempt it, and let the refusal be the answer. The
      //    cost is one round trip that returns 4xx; the benefit is that no cache
      //    freshness question exists on this path any more.
      //
      //    Never a SECOND run: adding a document mid-analysis is legitimate (a
      //    bando publishes errata), and must not launch another paid job behind
      //    the first.
      if (!jobIdRef.current) {
        setAnnouncement(
          `${created.length} ${plural(created.length, "documento riconosciuto", "documenti riconosciuti")}. Controllali e avvia l'analisi.`,
        );
      } else {
        setAnnouncement(
          `${created.length} ${plural(created.length, "documento aggiunto", "documenti aggiunti")}. L'analisi non riparte.`,
        );
      }
    } catch (e) {
      // No `missingRolesFromError` here any more: with the auto-start gone,
      // nothing in this function can produce a 422 carrying `missing_roles`.
      // Its one source was `beginAnalysis`, which now lives in `startRun`.
      setError(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function beginAnalysis(projectId: string, confirmLoss?: string): Promise<void> {
    // The backend dedups (one active run per project), so a double-click or a
    // retry after reload safely re-latches onto the existing job.
    // Goal 55B, D16: `confirmLoss` only when the user pressed «Avvia comunque».
    const j = await startAnalysis.mutateAsync(confirmLoss ? { projectId, confirmLoss } : projectId);
    startedAtRef.current = Date.now();
    setElapsed(0);
    jobIdRef.current = j.id;
    setStartedJobId(j.id);
  }

  /**
   * How many staged files have NOT reached the server yet.
   *
   * Declared up here, above `startRun`, because it is one of that function's
   * preconditions and not merely a thing the footer prints. A "failed" row
   * counts as pending: it is a file the user chose and the server does not
   * have.
   */
  const stagedPending = staged.filter((s) => s.state !== "uploaded").length;
  /**
   * Everything that must be true before a run may START, in one expression.
   *
   * This guard has now failed at two different call sites. Round 1 found that
   * the wizard's Avvia was live while files were still going up; the fix put
   * `busy || stagedPending > 0` on that button — and round 2 found the SECOND
   * caller, the retry inside the refusal notice, still open, because files can
   * be staged during the analyze round trip (`stagingLocked` tracks an upload
   * in flight, not a start in flight). A precondition repeated per call site is
   * one that will be missing at the next call site.
   *
   * So it is derived once, used by every control that can reach `startRun`, and
   * — load-bearing — re-checked INSIDE `startRun`, which is the authority. The
   * buttons carry it too, because a control that refuses silently is the
   * terminal-refusal shape this screen has already been burned by; each one
   * disables beside a sentence naming the file and the fix.
   */
  const sideUploading = listinoUpload !== null || missingUploadState !== null;
  const startBlock = startBlockedBy({
    canRunAnalysis,
    busy,
    sideUploading,
    stagedPending,
    missingPriceBook,
  });
  const startBlocked = startBlock !== null;
  /**
   * The ONE place an analysis is started, from every control that starts one.
   *
   * There used to be two — the auto-start inside `submit()` and `retryAnalysis`
   * — and the split cost a real defect: replacing the client-side role
   * prediction put the 422 handling on the second path and left `setMissingRoles([])`
   * behind on the first, so a retry that SUCCEEDED still rendered "Analisi non
   * avviata — manca: Nuovi Prezzi" beside a live "12% · trascorso 0s". Derived
   * state has to be re-derived at every source that can change it; the cheapest
   * way to guarantee that is to have one source.
   *
   * **Order is load-bearing: the analysis first, the Knowledge Base second.**
   * The analysis call is the one that can be REFUSED — the server answers 422
   * with `detail.missing_roles` — and a refusal must leave nothing spent. The
   * other way round, a bundle missing its Nuovi Prezzi would have had eighteen
   * documents synthesised into the tenant's Knowledge Base (real money, and a
   * shared store) underneath a screen correctly reporting that the analysis
   * never started. So the KB is reached only past a `return`.
   *
   * `kb.submit()` handles its own failure — it sets `kb.error` and never
   * throws — so a Knowledge Base that fails AFTER the analysis started does not
   * unwind a paid run. The panel's own "Conferma la selezione" bar renders from
   * that moment on (the wizard is over), which is where the KB alone is retried.
   */
  async function startRun(withKb: boolean, confirmLoss?: string): Promise<void> {
    // Re-asserted here rather than trusted to the buttons — and this line is
    // UNFALSIFIABLE, which is worth saying out loud rather than leaving as an
    // implied guarantee.
    //
    // Every caller's `disabled` already includes `startBlocked`, computed in the
    // SAME render closure as the value read here, so no reachable click can
    // arrive with the two disagreeing: deleting this clause leaves the whole
    // suite green (measured, 652/652). It cannot catch a stale-render click
    // either, for the same reason — the two values are the same value.
    //
    // An earlier version of this comment claimed the Passo-1 asymmetry was the
    // reason it exists. That was false: on Passo 1 the notice button's onClick
    // is `setStep(2)` and never calls `startRun` at all, so the asymmetry cannot
    // produce a blocked call. What this line actually buys is one thing —
    // the NEXT caller, added without the `disabled` prop, refuses instead of
    // spending. This guard has already been forgotten at a call site twice.
    if (!project || startingRef.current || startBlock !== null) return;
    startingRef.current = true;
    setError(null);
    setMissingRoles([]);
    setWorkLoss(null);
    try {
      await beginAnalysis(project.id, confirmLoss);
    } catch (e) {
      // Goal 55B, D16 — the server refused because this run would delete the user's work on
      // the saved result. Show what would be lost; start again ONLY on «Avvia comunque»,
      // with the token of the state shown. Nothing here retries by itself.
      const loss = userWorkRefusalFromError(e);
      if (loss) {
        setWorkLoss({ ...loss, withKb });
        setAnnouncement(loss.message);
        startingRef.current = false;
        return;
      }
      // The server's 422 IS the missing-roles answer — the only authoritative
      // one. Nothing here predicts it.
      const missing = missingRolesFromError(e);
      if (missing) {
        setMissingRoles(missing);
        // Back to Passo 1, because that is the step this refusal is ABOUT.
        //
        // The notice names the missing role and offers two remedies: add the
        // file, or correct the type of one already uploaded. The second lives
        // in the "Richiesti dall'analisi" group — which carries a row reading
        // "Nuovi Prezzi (AP/NP) — nessun documento riconosciuto" — and that
        // group is COLLAPSED on Passo 2. Leaving the user there would print an
        // instruction beside a hidden control: the same shape as a refusal with
        // no exit, one disclosure triangle thinner.
        //
        // ...but ONLY where the wizard exists. This catch is shared by all three
        // callers, and from «Riprova analisi» on the failure screen `jobId` is
        // already set, so no wizard renders: `setStep(1)` moved invisible state
        // and the live region told a screen-reader user to go back to a step
        // that is not on the page. The notice itself still renders there, naming
        // the role; the remedy is the review list below and the retry already
        // in hand.
        const inWizard = !jobIdRef.current;
        const names = missing.map((r) => DOC_ROLE_LABEL[r]).join(", ");
        if (inWizard) setStep(1);
        // D-3 — and in REVIEW mode, move the strip to Passo 1 too. The notice
        // says "correggi il tipo di un documento già caricato dall'elenco del
        // Passo 1" — it read "qui sotto" until this round changed it; on the
        // Avanzamento tab, which is where every started run opens, there is
        // nothing below it. Measured: rows matching "nessun documento
        // riconosciuto" = 0 before, 1 after one tab click. The sentence was
        // written for a layout where the panel was always underneath it.
        else setViewTab("1");
        setAnnouncement(
          `L'analisi non è partita: ${plural(missing.length, "manca", "mancano")} ${names}. ` +
            (inWizard
              ? "Torna al Passo 1 per correggere."
              : // Named, not pointed at — one wording with the notice this
                // announces, which stopped saying "qui sotto" for the same reason.
                // Two different pointers for one remedy is how a reader starts
                // wondering whether they are two remedies.
                hideWizardRef.current
                ? // `P-79` — with the wizard gone there IS no Passo 1 to name.
                  // «Vedi i documenti del progetto» on the outcome card is the
                  // route, and it is the one this state actually has.
                  "Apri «Vedi i documenti del progetto» per correggerli, poi riprova."
                : "Correggi i documenti nell'elenco del Passo 1, poi riprova."),
        );
      } else {
        // A non-422 failure says nothing about the documents, so the step is
        // left alone: sending someone back to re-check four correct files
        // because the server had an internal error is a false diagnosis.
        setError(apiErrorMessage(e));
      }
      // Released on EVERY exit, including this one. A latch that survives a
      // refusal would make the refusal terminal — the exact defect the sticky
      // Avvia bar exists to prevent, reintroduced from the other side.
      startingRef.current = false;
      return;
    }
    // Released HERE — the moment the analysis has actually started — and not
    // after the Knowledge Base call.
    //
    // It used to be released last, justified by "from here the wizard is
    // unmounted, so nothing can press Avvia again". That is true of the wizard
    // and false of `startRun`'s third caller, «Riprova analisi» on the failure
    // screen: a `kb.submit()` that never SETTLES (it cannot throw, but it can
    // hang) left the latch stuck, and that button then did nothing at all,
    // silently, with no error and no message. A comment explaining why
    // something is safe is a falsifiable claim, and that one was false.
    //
    // Releasing early cannot reopen a double start: `jobId` is set by
    // `beginAnalysis` before it resolves, the wizard footer renders only while
    // `!jobId`, and the refusal notice's retry is gated on both `!jobId` and a
    // non-empty `missingRoles` — cleared at the top of this function. The KB
    // call carries its own `kb.isPending` guard.
    startingRef.current = false;
    setAnnouncement("Analisi avviata.");
    // `isKbAdmin` re-checked here and not only on the button: a caller without
    // `kb:approve` pressing Avvia must start the analysis and leave the KB
    // pending for someone who may decide it, never silently post a decision the
    // server would refuse.
    /* `!onlyReview`, and NOT `kbRidesAlong`.
       They are different facts and I conflated them for one commit. The label
       drops "e Knowledge Base" when `approve.length === 0` — nothing is being
       ADDED — but a batch of pure declines is still a real Knowledge Base write
       and must still go out. Reusing the label's predicate here silently stopped
       sending the skips when a user vetoed everything, which is the one case
       where their decision is the entire payload. Caught by `LBL-03`, whose name
       is the rule: "vetoing EVERYTHING is still a Knowledge Base write". */
    if (withKb && isKbAdmin && !onlyReview && kb.approve.length + kb.skip.length > 0) {
      await kb.submit();
    }
  }

  /**
   * The drag/drop handlers, defined ONCE and spread onto whichever shape the
   * drop zone is currently wearing (`P-37`/A5 gives it two). Two literal copies
   * would be two places for the `stagingLocked` guard to be forgotten, and the
   * one that would get forgotten is the compact strip — the shape that renders
   * precisely when an upload is in flight.
   */
  const dropHandlers = {
    onDragEnter: (e: DragEvent) => {
      e.preventDefault();
      if (!stagingLocked) setDrag(true);
    },
    onDragOver: (e: DragEvent) => e.preventDefault(),
    onDragLeave: () => setDrag(false),
    onDrop: (e: DragEvent) => {
      e.preventDefault();
      setDrag(false);
      if (stagingLocked) return;
      // Walk the tree rather than reading `dataTransfer.files`: a dropped
      // FOLDER contributes no File at all, so the old handler did nothing
      // for the one gesture the caption invites. filesFromDataTransfer
      // reads `items` synchronously before its first await, which is the
      // only window in which that list is still populated.
      const dt = e.dataTransfer;
      void filesFromDataTransfer(dt).then((dropped) => {
        if (dropped.length > 0) addFiles(dropped);
      });
    },
  };

  const filePicker = useFilePicker({ multiple: true, onFiles: addFiles });
  const folderPicker = useFilePicker({ multiple: true, directory: true, onFiles: addFiles });

  const progress = job?.progress ?? (jobId ? 0 : null);
  const activeStep = progress != null ? activePipelineStep(progress) : -1;
  const verdict = analysisDone && job ? completionDisplay(job) : null;
  /**
   * Does pressing Avvia also commit the Knowledge Base?
   *
   * Three conditions, and QA supplied two of them.
   *
   * Without `isKbAdmin` the button promises an ordinary member something the
   * server will refuse. Without a count it promises it on a bundle with nothing
   * pending. `canRunAnalysis` was missing and is latent today — every role
   * holding `kb:approve` also holds `analysis:run` — but a disabled button
   * reading "Avvia analisi e Knowledge Base" describes an action this caller
   * cannot take at all, and the role table is exactly the thing that changes.
   *
   * `approve.length`, not `approve.length + skip.length`: with every optional
   * document vetoed the batch is all skips, and the button read "…e Knowledge
   * Base" directly above "Nessun documento selezionato — confermando, nessuno
   * verrà sintetizzato." The skips are still SENT — declining is a decision and
   * is recorded — but the label must not claim a synthesis that will not happen.
   * A button naming a thing it will not do is the same class of lie as
   * "aggiunta alla Knowledge Base ✓" was.
   */
  /**
   * `&& !onlyReview` — the Knowledge Base rides along only when its list is
   * actually on screen.
   *
   * Not a block: the analysis starts either way, because nothing in the
   * Knowledge Base column may prevent one. What changes is the PRESS. With the
   * filter narrowing the list, Avvia renames itself «Avvia analisi», sends no
   * batch, and the documents stay pending for a later confirmation from Passo 2
   * or the documents page — reversible, and nothing is bought unseen. Measured
   * before this: the footer read "18 documenti · 1326 pagine da sintetizzare"
   * over a screen rendering zero Knowledge Base rows.
   */
  const kbRidesAlong = canRunAnalysis && isKbAdmin && kb.approve.length > 0 && !onlyReview;
  /**
   * Is there a Knowledge Base decision to REPORT, whatever the label says?
   *
   * Deliberately a different question from `kbRidesAlong`, and separating the
   * two is a fix for the fix. Narrowing the label to `approve.length` was right
   * — it must not promise a synthesis that will not happen — but the summary
   * line rode on the same flag, so vetoing every document made the sentence
   * "Nessun documento selezionato — confermando, nessuno verrà sintetizzato"
   * disappear along with the false promise. The label went honest and the screen
   * went quiet, which is a worse trade: the user is about to commit a batch of
   * pure declines and should be told so.
   */
  /*
   * KNOWN, RECORDED, NOT FIXED — a role holding `kb:approve` WITHOUT
   * `analysis:run` gets checkboxes, a summary and no control anywhere able to
   * send them: Avvia is refused by `startBlock === "role"`, and the panel's own
   * "Conferma la selezione" bar is suppressed until a run exists. It is
   * unreachable under today's role table (every role with `kb:approve` also has
   * `analysis:run` — see `capabilities.py`), which is why it is a note and not
   * a fix. It is written here rather than in a findings page because the role
   * table is exactly what `ACC-11`/`ACC-12` are changing, and whoever splits
   * those two capabilities will be editing this line.
   */
  const kbBatchPending = isKbAdmin && kb.approve.length + kb.skip.length > 0;
  const startLabel = kbRidesAlong ? "Avvia analisi e Knowledge Base" : "Avvia analisi";
  /**
   * What the staging list shows.
   *
   * In phase 1 that is everything, including the "caricato ✓" rows — the user
   * wants to watch the bundle land. From phase 2 on, an uploaded file is already
   * a row in the review panel below, classified and with its real type, so
   * keeping it here too printed all thirty documents twice. Only what is still
   * waiting to be sent stays.
   */
  const stagedVisible = phase === 1 ? staged : staged.filter((s) => s.state !== "uploaded");
  const stagedFailed = staged.filter((s) => s.state === "failed").length;
  const hasArchive = staged.some((s) => isArchive(s.file));
  const kbReachedKb = documents.some((d) =>
    ["queued", "forwarding", "forwarded"].includes(d.kbForwardState),
  );

  /**
   * `P-37`/B1 + C1 — the tab strip.
   *
   * TWO tabs while the run is still waiting to be started, THREE once it exists:
   * Passo 1 · Passo 2 · Avanzamento (which becomes Esito in Fase 3). The strip
   * is the fix for the thing QA hit in three separate rounds — on Passo 2 there
   * was no visible evidence that Passo 1 existed, because Passo 2 REPLACED it.
   *
   * `activeTab` reads from `step` or from `viewTab` depending on the mode, so
   * there is exactly one answer at any moment; see `viewTab`'s own note.
   */
  /**
   * D-1 — the strip must carry the same freeze the footer button carries.
   *
   * R3-WIZ-01 froze «Passo 1» while a start is in flight, because moving back to
   * step 1 puts «Niente è ancora partito.» on screen with a paid `POST /analyze`
   * open. It froze the CONTROL, not the transition — and `P-37` then added a
   * second control for the same transition. Measured: the footer button reported
   * `disabled: true` while the tab reported `disabled: false`, and one tab click
   * took the false sentence from 0 nodes to 1.
   *
   * The guard lives in `pickTab`, which every tab click and both arrow-key paths
   * funnel through, so there is exactly one place to forget. `disabled` on the
   * buttons is the visible half; it is deliberately NOT the only half, because a
   * gate that only exists as an attribute leaves the branch below unreachable
   * and therefore untested.
   */
  const tabsFrozen = awaitingStart && (startAnalysis.isPending || kb.isPending);
  /** Documents still awaiting a Knowledge Base decision — ONE expression, read
   *  by the track card, the outcome card and the commit button's own count. */
  const kbAwaiting = kb.optional.length + kb.lockedIn.length;
  /** Forwards the SERVER could not complete — distinct from `kb.error`, which is
   *  this client's request being refused. A batch accepted and then failed
   *  downstream sets this and never sets that. */
  const kbFailed = documents.filter((d) => d.kbForwardState === "failed").length;
  /**
   * `P-79` — in Fase 3 the wizard strip is REMOVED, and the two Processo
   * modules plus the outcome card are the whole screen.
   *
   * Omar, 12 Sep, from `a_summary.png`: *"Remove Passo 1 Computo, PASSO 2
   * Knowledge Base, ESITO • Completato and just leave the modules «PROCESSO 1
   * Analisi del computo» and «PROCESSO 2 Knowledge Base»."*
   *
   * The strip was not merely redundant, it was making a FALSE CLAIM. Its third
   * tab printed «Esito ✓ Completato» from `phase`, and `phase` is derived from
   * `job.status` alone with zero Knowledge Base input — so on the screenshot it
   * read "Completato" while Processo 2 was still running. The two lanes beside
   * it already carry both tracks, each with its own state.
   *
   * ONE EXCEPTION, and it is not a hedge — it is the reachability the strip was
   * the only carrier of. The Processo 2 lane REPORTS «N documenti in attesa
   * della tua conferma» and holds no control: the Knowledge Base commit lives
   * on Passo 2, in `IntakeReviewPanel`. Removing the strip whenever that is
   * outstanding would leave a lane naming an action with no way to take it, and
   * the same is true of files staged and never sent. So the strip survives in
   * exactly those two states and disappears in every other — including the one
   * on Omar's screenshot, «18 inviati · 0 esclusi», where there is nothing left
   * to reach.
   *
   * The FALSE CLAIM is gone either way: the strip's `phase === 3` kicker and
   * title branches («Esito», «Completato» / «Non riuscita») are deleted, so
   * where it does still render the third tab reads «Avanzamento» with the
   * analysis percentage — a number about the analysis, never a verdict on both
   * tracks.
   */
  const esitoWorkOnlyReachableFromTheWizard = kbAwaiting > 0 || stagedVisible.length > 0;
  const hideWizard = phase === 3 && !esitoWorkOnlyReachableFromTheWizard;
  const showTabs = phase !== 1 && !!project && !hideWizard;
  /**
   * Resolved in ONE place rather than by branching each consumer. With the
   * strip gone nothing can move `viewTab` any more, so a reader who happened to
   * be parked on Passo 1 when the job finished would otherwise see the Passo 1
   * panel and the outcome card at once — and `panelMounted`, the lanes' own
   * gate and `aria-labelledby` would each have needed their own copy of the
   * rule. Five consumers, one expression.
   */
  const activeTab: "1" | "2" | "3" = hideWizard
    ? "3"
    : awaitingStart
      ? step === 2
        ? "2"
        : "1"
      : viewTab;
  // Mirrored for `startRun`, which runs earlier in the file than this line and
  // must announce the same remedy the notice below prints. Assigned during
  // render rather than in an effect: the click that reads it always happens
  // after a render, and an effect would leave the FIRST click reading a stale
  // value — which is the one click that matters on a failed run.
  hideWizardRef.current = hideWizard;
  const tabIds: ("1" | "2" | "3")[] = awaitingStart ? ["1", "2"] : ["1", "2", "3"];
  /**
   * Whether the review panel is rendered — ONE expression, read by the panel's
   * own gate and by every notice that stands in for a surface the panel carries.
   *
   * Written as a partition rather than as two hand-kept conditions on purpose:
   * the KB-error notice and the panel's copy of the same message were once
   * written as a partition and were not one, and a composed state put the
   * message on screen twice. It was unreachable by luck rather than by
   * construction, which is not a property to leave standing on a money screen.
   */
  const panelMounted = phase !== 1 && !!project && activeTab !== "3";
  function pickTab(id: "1" | "2" | "3"): void {
    if (tabsFrozen) return;
    if (awaitingStart) {
      if (id !== "3") setStep(id === "2" ? 2 : 1);
      return;
    }
    setViewTab(id);
  }
  /* Arrow-key movement across the strip. A `role="tablist"` whose tabs cannot be
     reached with the arrow keys is a tablist in name only — it announces a
     keyboard contract to a screen-reader user and then does not honour it. */
  function tabKeyDown(e: KeyboardEvent<HTMLButtonElement>, id: "1" | "2" | "3"): void {
    const i = tabIds.indexOf(id);
    let next = -1;
    if (e.key === "ArrowRight") next = (i + 1) % tabIds.length;
    else if (e.key === "ArrowLeft") next = (i - 1 + tabIds.length) % tabIds.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = tabIds.length - 1;
    if (next < 0) return;
    e.preventDefault();
    pickTab(tabIds[next]);
    document.getElementById(`intake-tab-${tabIds[next]}`)?.focus();
  }

  const notices: { tone: Tone; node: JSX.Element }[] = [];
  if (error) notices.push({ tone: "err", node: <>{error}</> });
  /* A Knowledge Base failure used to be announced ONLY inside the review panel,
     beside the control that retries it. That was sound while the panel was
     always on screen. `P-37`/C1 made Avanzamento the default view of a started
     run, and a started run is exactly when a kb-forward fails — so the alarm
     became silent on the one screen the user is actually looking at.

     Raised here only when the panel is NOT showing, so the message never
     appears twice: on Passo 2 the panel's own copy is the better one, because
     the retry button is next to it. Measured by ORD-03, which reported
     `alerts: []` on a run whose kb-forward had thrown. */
  /**
   * A finished run announces itself wherever the reader is standing.
   *
   * Decided by Omar 2026-08-20. A run that FAILED pushed a notice and a run that
   * SUCCEEDED pushed nothing, so a reader parked on Passo 1 or Passo 2 learned
   * about a 1–3 hour job finishing only from a badge glyph and a sidebar
   * heading. QA measured `alerts: 0 → 0` on completion against
   * `["Analisi non riuscita: …"]` on failure.
   *
   * `tone: "info"`, and no tick. This is the exact trap `P-32` fell into: told
   * to stop showing the reconciliation verdict, I emitted the verdict's POSITIVE
   * token — `tone: "ok"` with a ✓ — which turned every state green, including a
   * run whose Audit banner was red about € 2,9M. Colour and a checkmark are
   * claims about the numbers. This notice claims only that the job ended and
   * says where the numbers are; the Audit banners remain the only thing allowed
   * to say whether they add up.
   *
   * Gated off tab 3, where the outcome card already carries it.
   */
  if (analysisDone && activeTab !== "3") {
    notices.push({
      tone: "info",
      node: (
        <>
          L&apos;analisi è terminata. Apri <strong>Esito</strong> per vedere com&apos;è andata.
        </>
      ),
    });
  }
  /* `!== "2"`, not `=== "3"`. The panel's own copy of this message rides on the
     commit bar, and R4-02 has just gated that bar to Passo 2 — so on Passo 1 a
     Knowledge Base failure would have had no surface at all. Exactly one carries
     it in every state: the panel on Passo 2, where the retry button is beside
     it, and this notice everywhere else. */
  /* `&& !!jobId` closes L-1. The panel carries this message from TWO places: the
     commit bar (Passo 2 of a started run) and a `!showCommit` block that renders
     on any tab before a run exists. Without the third clause the two gates were
     written as a partition and were not one — a composed wizard state put the
     message on screen twice. It was unreachable by luck rather than by
     construction, which is not a property to leave standing on a money screen. */
  /* A REFUSED type correction, wherever the panel that would report it is not
     rendered. Exactly the shape used for `kb.error` below, and gated on the
     NEGATION of the panel's own condition rather than on a second hand-written
     guess at when the panel is absent — so the two cannot both fire and cannot
     both stay silent. `grep "Correzione non salvata"` returned one product line
     and zero tests: this failure path had no coverage at all. */
  if (correction.error && !panelMounted) {
    notices.push({
      tone: "err",
      node: (
        <>
          {/* Makes NO claim about WHICH field. `useDocumentCorrection.correct()`
              sets one `error` for both the kind select and the role select, so
              "il tipo del documento è rimasto quello di prima" described a
              refused ROLE change as if it had been a type change — one failure
              worded two different ways depending on which tab you stood on, and
              the tab-3 wording naming a field the PATCH never carried. The
              panel's own line got this right; this one did not. */}
          Correzione non salvata: {correction.error} Niente è stato modificato.
        </>
      ),
    });
  }
  if (kb.error && activeTab !== "2" && !!jobId) {
    notices.push({
      tone: "err",
      node: (
        <>
          {/* No "qui sopra". The strip is BELOW this notice in DOM and visual
              order — a plain flex column with no `order` anywhere — so the
              direction was simply wrong. Naming the tab is enough. */}
          {kb.error} L&apos;analisi del computo non è toccata e prosegue.{" "}
          {/* `P-79` — «Apri il Passo 2 per riprovare» only where Passo 2 is on
              screen. With the wizard gone it names a control that does not
              exist, which is the defect this file records by name. */}
          {!hideWizard ? (
            <>
              Apri il <strong>Passo 2</strong> per riprovare.
            </>
          ) : null}
        </>
      ),
    });
  }
  if (analysisFailed) {
    notices.push({
      tone: "err",
      /* Goal 55B — «Analisi non salvata: …» / «Analisi fermata …» stand on their own and are
         shown as they are; any other error keeps the frame (`lib/jobFailure.ts`). */
      node: <>{failureLine("Analisi non riuscita", job?.error)}</>,
    });
  }
  // A refusal is a statement about a MOMENT — the last attempt — and it is said
  // so, rather than as a claim about the present.
  //
  // The first attempt at this filtered the warning against `documents`, hiding
  // any role the cached list showed as present. That is worse than the problem:
  // it lets a stale cache SUPPRESS a refusal the server has just issued, which
  // is the inverse of why the prediction was deleted in the first place. Its own
  // control leg caught it.
  //
  // So the sentence keeps the server's word, timestamps itself in words, and
  // carries the control to ask again — which is what actually makes it
  // self-correcting, since every attempt clears it and the next answer replaces
  // it.
  const stillMissing = missingRoles;
  if (stillMissing.length > 0) {
    notices.push({
      tone: "warn",
      node: (
        <>
          Analisi non avviata —{" "}
          {/* `P-47` — the ZIP wording is scoped to the roles a ZIP can actually
              carry. `extract.py` states the opposite fact about the listino: "a
              tender download NEVER contains a listino", so «nello ZIP non
              abbiamo trovato: Listino regionale» blamed the download for an
              absence that is by design. */}
          {hasArchive && !stillMissing.every((r) => r === "regional")
            ? "nello ZIP non abbiamo trovato"
            : plural(stillMissing.length, "manca", "mancano")}
          : {stillMissing.map((r) => DOC_ROLE_LABEL[r]).join(", ")}.{" "}
          {/* And the remedies must name a control that exists for the role
              actually missing. Both of the originals are document-shaped —
              upload, or reclassify — while `P-47b`'s whole justification is
              that the library picker is the exit for the four cases where
              auto-association declines. A user who cannot produce an .xlsx was
              pointed at neither thing that would unblock them. */}
          {stillMissing.includes("regional") ? (
            <>
              Per il listino puoi caricarlo o sceglierne uno dalla libreria dal{" "}
              <strong>{hideWizard ? "elenco dei documenti del progetto" : "Passo 1"}</strong>.{" "}
            </>
          ) : null}
          {stillMissing.some((r) => r !== "regional") ? (
            <>
              Aggiungi i file mancanti, oppure correggi il tipo di un documento già caricato
              dall&apos;elenco{" "}
              <strong>{hideWizard ? "dei documenti del progetto" : "del Passo 1"}</strong>.{" "}
            </>
          ) : null}
          Questo è l&apos;esito dell&apos;ultimo tentativo: dopo una correzione, riprova.
          {/* "qui sotto" pointed at the review panel, which `P-37` gates off
              tab 3 — so on Esito the sentence named two remedies and the tab
              offered neither: QA enumerated `3F×3` and measured ZERO «Correggi»
              controls against four on `3F×1`. Naming the step is what the
              KB-error notice and the outcome card already do; this was the last
              notice on the screen still pointing with a direction. */}
          {/* The remedy the sentence recommends has to be REACHABLE. Correcting a
              type uploads nothing, so the commit bar stays hidden (it needs
              something pending) and "Riprova analisi" stays hidden (it needs
              phase 3) — leaving a screen that names the fix and offers no way to
              apply it. Same terminal shape the deleted prediction used to reach
              by a different route: the defect was never the prediction, it was
              that a refusal had no exit. */}
          {/* On Passo 1 this MOVES to Passo 2 rather than starting from here.
              Starting would commit the Knowledge Base batch — `startRun(true)`
              — while the group listing those documents is collapsed one step
              away, i.e. spending money from a screen that is not showing what
              is being bought. The rule the wizard rests on is that the group
              carrying a commit is expanded on the step whose button commits it.
              Two clicks, both of which show the user what they are agreeing to,
              beats one that does not. */}
          {/* D-2 — `!jobId` alone hid this on the one screen that most needs it.
              A run that FAILED has a `jobId`, so on Passo 1 of a failed run the
              refusal named a remedy and every control able to act on it was
              elsewhere: «Riprova analisi» lives inside the tab-3 outcome card,
              and this button was gone. Round 1 measured four refusal states with
              `3F×1` the only one carrying no exit; D-3 has since made a refusal
              landing on `3F×3` unreachable, so do not read that table as a
              description of states the product can still produce. A
              failed run retries with `startRun(false)`: there is no wizard left
              to be on, and the Knowledge Base has its own commit on Passo 2, so
              a retry has no business touching it.

              This used to justify itself with "the Knowledge Base was already
              committed at the first start". That is false on three reachable
              paths — the starter was a member and `startRun` skipped the commit,
              the batch was empty, or `kb.submit()` rejected — and the danger of
              a false justification is not the sentence, it is the maintainer who
              believes it and prunes the Passo 2 bar for started runs. */}
          {project && (!jobId || (analysisFailed && activeTab !== "3")) && (
            <button
              className="btn btn-sm"
              style={{ marginLeft: 10, height: 26 }}
              onClick={() =>
                analysisFailed
                  ? void startRun(false)
                  : step === 1
                    ? setStep(2)
                    : void startRun(true)
              }
              // On Passo 1 this only NAVIGATES, so an unsent file must not
              // disable it — the file is sent from the commit bar one step
              // back, and blocking the way there would be a refusal with no
              // exit again. On Passo 2 it starts a run and carries the same
              // preconditions as the sticky Avvia. This asymmetry is the whole
              // reason `startRun` re-checks them itself.
              disabled={
                startAnalysis.isPending ||
                kb.isPending ||
                !canRunAnalysis ||
                ((analysisFailed || step === 2) && startBlocked)
              }
            >
              <I.Refresh size={12} />{" "}
              {/* The SAME words as the outcome card's control, because it is
                  the same action — and the two are never co-present: the card
                  is tab-3 only and this is gated off tab 3. Two nearly-identical
                  labels ("Riprova analisi" / "Riprova l'analisi") for one action
                  is how a reader starts wondering whether they differ.
                  At REST only: the card renames itself to «Avvio in corso…»
                  while pending, this one only disables. */}
              {analysisFailed
                ? "Riprova analisi"
                : step === 1
                  ? "Vai al Passo 2 per riprovare"
                  : "Riprova ad avviare l'analisi"}
            </button>
          )}
        </>
      ),
    });
  }
  // Shown in EVERY phase. It was phase-1-only, which meant that the moment the
  // analysis started — the moment the phase advances — the one sentence telling
  // the user those documents are PROPOSED and not yet added disappeared. That
  // note is the guard against the most expensive lie this screen can tell, and it
  // must not be the first thing a phase transition removes.
  // Derived from the SERVER's list wherever there is one, so it cannot outlive
  // the fact it states; `proposedCount` covers only the window before the first
  // list arrives.
  const kbNote = kbProposedNote(
    documents.length > 0
      ? documents.filter((d) => d.kbForwardState === "pending").length
      : proposedCount,
  );
  if (kbNote) notices.push({ tone: "info", node: <>{kbNote}</> });
  // Goal 55B, D16 — the refusal names every loss by type and count (the server's words), and
  // the confirmation is a separate, explicit press.
  if (workLoss) {
    notices.push({
      tone: "warn",
      node: (
        <>
          <strong>Analisi non avviata:</strong> il risultato salvato contiene lavoro che
          l&apos;analisi cancellerebbe quando salva il nuovo risultato:
          <ul style={{ margin: "6px 0 6px 18px", padding: 0 }}>
            {workLoss.lines.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          Avviando comunque, questo lavoro andrà perso. Se modifichi il risultato salvato mentre
          l&apos;analisi è in corso, l&apos;analisi non verrà salvata e il risultato resterà
          com&apos;è.
          <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
            <button
              className="btn btn-sm"
              onClick={() => void startRun(workLoss.withKb, workLoss.confirmToken)}
              disabled={startAnalysis.isPending || kb.isPending || startBlocked}
            >
              Avvia comunque
            </button>
            <button className="btn btn-sm btn-ghost" onClick={() => setWorkLoss(null)}>
              Annulla
            </button>
          </div>
        </>
      ),
    });
  }
  /* `P-45` — the partial-ZIP notice (Omar ratified "Option A", 2026-08-24).
     Its whole job is to let the user do the thing they described wanting to do:
     look at the document list and check nothing is missing.

     `warn`, not `err`: nothing failed — files came in — and the render site
     gives `err` a `role="alert"`, which is the channel the ALL-skipped failure
     already owns. Two live alerts saying different things about the same upload
     is how the real one stops being read. And not `ok`: a ✓ over "28 of 30" is
     the `P-32` trap exactly, a positive token asserting a state nobody checked.

     Not phase 1: the upload has not committed and there is no list to check
     against yet. Every phase after that, INCLUDING the completion screen.

     CORRECTED 2026-08-25, and the correction is the point. This gate first read
     `phase === 2`, justified in this comment by "the completion screen carries
     its own verdicts". An independent QA pass rendered it and the claim is
     FALSE: `completionVerdict` above branches only on `reconciliationOf` and
     `categoryReconciliationOf` — money — and a grep for anything naming a lost
     or missing file across the whole of `src/` outside this block returns
     nothing. So the sequence was: a ZIP loses two files, the user is told, the
     user starts a run that takes one to three hours, and the fact is silently
     dropped at exactly the moment they begin reading totals derived from it.
     Reconciliation cannot cover for it either — it checks the extraction
     against the CME's OWN stated total, so a document that never arrived
     reconciles perfectly.

     The trailing sentence changes with the phase because the remedy does. Before
     the run it is an instruction; after it, the run is already spent and the
     honest thing is to say what it was spent on. */
  if (phase !== 1 && partialZips.length > 0) {
    notices.push({
      tone: "warn",
      node: (
        <>
          {partialZips.join(" ")}{" "}
          {phase === 3
            ? "L'analisi è stata eseguita su questi documenti."
            : "Controlla che i documenti necessari ci siano tutti."}
        </>
      ),
    });
  }
  // The phase-2 upload receipt. Only where the staging row has already left the
  // list — in phase 1 the row itself, marked "caricato ✓", is the receipt — and
  // only until the documents it promises have actually appeared. It said
  // "compaiono qui sotto appena riconosciuti" indefinitely, including on the
  // finished-analysis screen, long after they had.
  const receiptLive = documents.length <= receiptBaseline && phase !== 3;
  if (phase !== 1 && receiptLive && justUploaded.length > 0) {
    notices.push({
      tone: "ok",
      node: (
        <>
          {justUploaded.length}{" "}
          {plural(justUploaded.length, "documento caricato", "documenti caricati")}:{" "}
          {justUploaded.slice(0, 6).join(", ")}
          {justUploaded.length > 6 && ` e altri ${justUploaded.length - 6}`}. Compaiono
          nell&apos;elenco del <strong>Passo 1</strong> appena riconosciuti.
          {/* Not "qui sotto". The drop zone stays live for the whole of a
              started run — adding a document mid-run is a real thing to do —
              and every started run OPENS on Avanzamento, where the panel this
              sentence points at is not rendered. Same defect as the refusal
              notice above it; found by grepping the class rather than the diff,
              which is the only way the third instance ever turns up. */}
        </>
      ),
    });
  }

  return (
    <div
      style={{
        padding: "28px 36px 60px",
        /* `P-37`/A8 — Fase 1 is ONE column in the prototype, and narrower. Both
           halves of that matter: the explainer below names the two passi and
           lists the four pipeline stages, which is exactly what the sidebar
           card says. Keeping both would put the same list on screen twice, and
           the sidebar's copy is the worse one here — in Fase 1 every stage is
           pending, so it draws four empty circles that assert nothing. */
        maxWidth: phase === 1 ? 760 : 1100,
        margin: "0 auto",
        width: "100%",
        // `--tr-slow` is not defined anywhere in this repo; only the fallback ran.
        transition: "max-width 350ms ease",
      }}
    >
      {/* Two separate lies lived on this one control, and both are about the word
          "Annulla".

          (1) It stayed live for the whole `POST .../analyze`, because its label is
          derived from `analyzing`, which needs `jobId` — and `jobId` lands only on
          the RESPONSE. Pressing it unmounts `UploadView` outright (`IngestPage`
          swaps the view), while the request already in flight starts the paid run
          and `startRun` carries on down a detached closure to commit the Knowledge
          Base batch. Nothing was cancelled; the user was simply no longer watching.
          This is the same defect round 3 found on «Indietro» — I fixed the one
          control the test happened to press.

          (2) Even at rest on Passo 2 it says "Annulla" over a screen where the
          project and up to ~30 documents already exist server-side. `onBack` does
          not delete them; it discards the only route back to them, since no URL
          carries a project id into this wizard. The label is keyed on `project`
          rather than on `analyzing`: `project` is set once from the create
          response and never cleared, so it is exactly "something exists now".

          RESOLVED 2026-09-02 by `P-63`: the wizard has an address now
          (`/projects/:projectId/prepare`), so `onBack` no longer discards the
          only route to those documents.

          The sentence this replaces claimed the work was "recorded as `P-31b`".
          It never was — `grep -rn "P-31b"` over the wiki and all four repos
          returned exactly one hit, this comment. A reference to a filing that
          does not exist reads as a filing that does, and it survived three
          weeks. See [[CME_Studio_roadmap]] `P-63`. */}
      <button
        className="btn btn-ghost btn-sm"
        onClick={onBack}
        style={{ marginBottom: 16 }}
        disabled={startAnalysis.isPending || kb.isPending}
      >
        <span style={{ transform: "scaleX(-1)", display: "inline-flex" }}>
          <I.Arrow size={13} />
        </span>{" "}
        {analyzing || startAnalysis.isPending
          ? "Torna al workspace (l'analisi continua)"
          : project
            ? "Torna al workspace (il progetto è salvato)"
            : "Annulla"}
      </button>

      <div className="t-eyebrow" style={{ marginBottom: 6 }}>
        {phase === 1
          ? "Nuovo progetto · Fase 1 di 3 — carica e riconosci"
          : phase === 2
            ? awaitingStart
              ? /* The POST is open and `jobId` lands only on the response, so
                   `awaitingStart` stays true for the whole request — and this
                   read "conferma e avvia" beside a card already reading «Avvio
                   in corso…». The card's half of that pair was fixed a round
                   ago; the eyebrow's and the lede's were not. */
                startAnalysis.isPending
                ? "Fase 2 di 3 — avvio in corso"
                : "Fase 2 di 3 — conferma e avvia"
              : "Fase 2 di 3 — analisi in corso"
            : "Fase 3 di 3 — esito"}
      </div>
      {/* `P-39` `T4`/`F24` — `lineHeight` is not decorative here. Tailwind's
          preflight resets a heading's size and weight but NOT its leading, so
          without this the 26px title inherits the body's 1.5 and sits in a
          39px line box instead of the prototype's 32.5px. On a two-line
          project name that is 13px of drift, and the title stops reading as
          one block with the eyebrow above it. */}
      <h1
        style={{
          fontSize: 26,
          fontWeight: 600,
          letterSpacing: "-0.02em",
          lineHeight: 1.25,
          margin: "6px 0 8px",
          textWrap: "balance",
        }}
      >
        {phase === 1 ? "Carica i documenti della gara" : (project?.name ?? "Progetto")}
      </h1>
      {/* `P-39` `F4`/`T1`/`L5-12` — this sentence explains the whole screen and
          was printing in `--c-ink-3`, the SAME grey as the small-caps eyebrow
          directly above it, at full column width. The header ladder inverted:
          the prototype reads light eyebrow → black title → dark explanation,
          ours read grey → black → the same grey again. `--c-ink-2` is the
          prototype's `#3d424e` exactly, and the measure stops the line running
          1100px in Fase 2/3 when the container widens. */}
      <p
        style={{
          color: "var(--c-ink-2)",
          fontSize: 13.5,
          maxWidth: 640,
          textWrap: "pretty",
          marginTop: 0,
          marginBottom: 24,
        }}
      >
        {phase === 1 ? (
          <>
            Carica lo ZIP del bando. Lo apriamo, riconosciamo ogni documento e ti mostriamo cosa
            abbiamo trovato. L&apos;analisi <strong>non parte da sola</strong>: prima controlli il
            riconoscimento, poi la avvii tu.
          </>
        ) : phase === 2 ? (
          awaitingStart ? (
            startAnalysis.isPending ? (
              <>Stiamo avviando l&apos;analisi. Un momento: non chiudere questa pagina.</>
            ) : (
              <>
                Abbiamo letto {documents.length} documenti. Controlla che siano quelli giusti,
                scegli cosa entra nella Knowledge Base e avvia: l&apos;analisi non parte da sola.
              </>
            )
          ) : (
            <>
              L&apos;analisi è in corso. Gira sul server: puoi chiudere il browser e tornare quando
              vuoi.
            </>
          )
        ) : (
          <>Ecco com&apos;è andata, e dove puoi continuare.</>
        )}
      </p>

      {/* `P-37`/A7 — the three phases, drawn. The only thing on this screen that
          tells a first-time user the upload is one third of a sequence rather
          than the whole errand. `aria-hidden`: the same position is already in
          the eyebrow above ("Fase 2 di 3"), in words, so a screen reader that
          read both would hear it twice. Numbers, not ticks, on every phase —
          including the ones behind us. A tick asserts an OUTCOME, and phase 1
          being behind us says only that we left it: a run can reach Fase 3
          having failed, and did in QA. */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          flexWrap: "wrap",
          marginTop: -12,
          marginBottom: 24,
        }}
        aria-hidden
      >
        {/* `!!jobId`, not `awaitingStart`. `awaitingStart` is `phase === 2 &&
            !jobId`, so in FASE 1 its first conjunct is false and the strip
            named phase 2 "Analisi in corso" — to a first-time user who has
            uploaded nothing, on a screen whose lede says, three lines above,
            that the analysis does not start by itself. The confirm-and-start
            step was missing from the map of the errand entirely. The real
            question is whether a run exists, which is what `jobId` answers. */}
        {phaseSteps(!!jobId, phase).map((label, i) => {
          const n = i + 1;
          const done = n < phase;
          const here = n === phase;
          return (
            <div key={label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: "50%",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 10,
                  fontWeight: 600,
                  background: done || here ? "var(--c-brand)" : "transparent",
                  color: done || here ? "#fff" : "var(--c-ink-3)",
                  border: `1px solid ${done || here ? "var(--c-brand)" : "var(--c-line-strong)"}`,
                }}
              >
                {n}
              </span>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 500,
                  color: here ? "var(--c-ink)" : done ? "var(--c-ink-2)" : "var(--c-ink-3)",
                }}
              >
                {label}
              </span>
            </div>
          );
        })}
      </div>

      {/* Everything that changes on this screen happens without a click. Nothing
          announced it to a screen reader before. */}
      <div aria-live="polite" className="sr-only">
        {announcement}
      </div>

      <div
        style={{
          display: "grid",
          /* `P-46` — one column, always. This reserved 320px for the
             «Pipeline AI» sidebar, keyed on `phase` INDEPENDENTLY of the
             sidebar's own gate. The sidebar is gone; a key left on `phase`
             would have held an empty 320px gutter open on every screen,
             and jsdom does no layout so nothing in the suite could see it. */
          gridTemplateColumns: "minmax(0, 1fr)",
          gap: 24,
          alignItems: "start",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
          {/* Two tracks, drawn as two things — the analysis nobody has to start,
              beside the decision nothing is waiting on.

              `P-37`/C1 made these the AVANZAMENTO TAB'S content rather than a
              permanent header. They restated the third tab a few centimetres
              above it — "In corso" appeared twice on one screen from two
              different sources, with no way for a reader to tell whether the two
              were about the same thing. They now show while the run is still
              waiting to be started (where there is no third tab) and whenever
              that tab is the one selected. On Passo 1 and Passo 2 of a started
              run they stand down: those panels are about the documents, and the
              strip's own badge already carries the percentage. */}
          {phase !== 1 && activeTab === "3" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
              {/* `P-39` `L4-07` — two colour-coded LANES, stacked, not two
                identical white cards side by side. They shipped with the same
                border, radius and shadow, a neutral grey eyebrow apiece and a
                3px stripe on the far left as the only thing telling them apart
                — and a stripe at the edge of a card is not how anyone reads
                which process they are looking at. The prototype tints each
                lane, heads it with a 32px tinted squircle, and colours the
                eyebrow and the title to match the track.

                The tile is absolutely positioned against the lane's padding
                rather than made a flex sibling, so the ~90 lines of existing
                status logic inside each lane are not reparented. Contained on
                purpose: this is a restyle, and moving that content is how a
                restyle turns into a defect. */}
              <div
                style={{
                  padding: "16px 18px",
                  background: "var(--c-ai-lane)",
                  border: "1px solid var(--c-ai-lane-line)",
                  borderRadius: 12,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  <span
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 9,
                      background: "var(--c-ai-soft)",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <I.Sparkle size={16} strokeWidth={2} style={{ color: "var(--c-ai)" }} />
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div className="t-eyebrow" style={{ color: "var(--c-ai)" }}>
                      Processo 1
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--c-ink)" }}>
                      Analisi del computo
                    </div>
                  </div>
                  {/* `P-39` `L4-07` — the headline figure, right-pinned, in the
                      track's own colour. This is the "15px coloured" element in
                      the finding; the last commit correctly identified it and
                      then did not build it, which is how a restyle ends up 90%
                      done and reads as finished. `tabular-nums` because it
                      ticks: without it every digit change shifts the glyphs
                      beside it. The detail line below keeps the same number
                      with the elapsed time — same quantity, two roles, never
                      two different quantities in the same words. */}
                  {analyzing && progress != null && (
                    <span
                      style={{
                        marginLeft: "auto",
                        fontSize: 15,
                        fontWeight: 600,
                        color: "var(--c-ai)",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {progress}%
                    </span>
                  )}
                </div>
                {/* `P-46` — the analysis progress bar, rehomed from the deleted
                  «Pipeline AI» sidebar, which was its ONLY renderer. It is the
                  wizard's only `role="progressbar"` for the analysis, and
                  `a_analysis.png` puts it exactly here: full lane width, under
                  the header row that already right-pins the percentage.

                  `progress != null`, NOT `analyzing` — a finished run keeps its
                  completed bar instead of having it disappear at the moment it
                  reaches 100%. The `aria-label` is carried over from the sidebar
                  unchanged on purpose: it is the only thing telling this bar
                  apart from the per-file upload bars, which share the role. */}
                {progress != null && (
                  <div
                    role="progressbar"
                    aria-valuenow={progress}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuetext={`${progress} per cento`}
                    aria-label="Avanzamento dell'analisi"
                    style={{
                      height: 6,
                      borderRadius: 3,
                      background: "var(--c-line)",
                      overflow: "hidden",
                      marginBottom: 10,
                    }}
                  >
                    <div
                      style={{
                        width: `${Math.max(progress, 3)}%`,
                        height: "100%",
                        borderRadius: 3,
                        background: analysisFailed ? "var(--c-err)" : "var(--c-ai)",
                        transition: "width 600ms ease",
                      }}
                    />
                  </div>
                )}
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: analysisFailed
                      ? "var(--c-err)"
                      : analysisDone
                        ? TONE[verdict?.tone ?? "info"].fg
                        : stillMissing.length > 0
                          ? "var(--c-warn)"
                          : "var(--c-ai)",
                  }}
                >
                  {analysisFailed
                    ? "Non riuscita"
                    : analysisDone
                      ? (verdict?.title ?? "Completata")
                      : stillMissing.length > 0
                        ? "Non partita — mancano dei documenti"
                        : jobId
                          ? "In corso"
                          : /* `P-46` — THE TWO BRANCHES BELOW ARE UNREACHABLE.
                               This lane renders under `phase !== 1 && activeTab
                               === "3"`, and tab 3 does not exist while
                               `awaitingStart` (`tabIds` omits it, :1543). So
                               inside this lane `jobId` is always set: the
                               `isPending` branch (true only before a job id
                               lands) and the `!jobId` else can never be taken.
                               Left in place rather than deleted — this is a
                               reachability argument about a gate, and deleting
                               product branches on an argument is how a cleanup
                               becomes a defect. Flagged for the adversarial
                               review to adjudicate. Their catalogue probes are
                               retired with this measurement in
                               `claimInventory.ts`. */
                            startAnalysis.isPending
                            ? // The card renders on Passo 2 as well, and `jobId` lands only
                              // on the response — so for the whole POST it read "Non ancora
                              // partita" beside a button reading "Avvio in corso…". Freezing
                              // «Indietro» removed the OTHER false sentence this pair used to
                              // make; this is the half that stayed, on the very step the
                              // freeze holds the user on.
                              "Avvio in corso…"
                            : "Non ancora partita"}
                </div>
                {analyzing && progress != null && (
                  <div style={{ fontSize: 11.5, color: "var(--c-ink-3)", marginTop: 3 }}>
                    {progress}% · trascorso {fmtElapsed(elapsed)} · stimate 1–3 ore
                  </div>
                )}
                {analyzing && (
                  <div style={{ fontSize: 11.5, color: "var(--c-ink-3)", marginTop: 4 }}>
                    Non devi fare niente. Continua sul server: puoi chiudere il browser.
                  </div>
                )}
                {/* `P-64` — the way out, INSIDE the analysis lane.
                    Omar, 2026-09-02: *"As soon as the Analysis is complete, a
                    button should appear directly below the Analysis progress
                    area… The user decides when to click it."*

                    The Esito card lower down already carries a button to the
                    same place, and that is not this. It sits below the OTHER
                    lane, fourth among four, and it answers "what now?" rather
                    than "is this one done?". A person watching a bar wants the
                    answer where the bar is.

                    Gated on `analysisDone` alone. Not on the Knowledge Base,
                    which is the ratified rule (`KB-11`, and Omar again today:
                    *"The Knowledge Base must never block access to the Workspace
                    once the Analysis is ready"*) — and not on `verdict` either,
                    which would tie the exit to the reconciliation branch and
                    could withhold it exactly when something needs looking at.

                    No automatic redirect anywhere on this screen, by the way,
                    and there never was: every `navigate()` in this file is
                    inside a control a person presses. Stated here because it is
                    now a REQUIREMENT rather than an accident. */}
                {analysisDone && project && (
                  <div
                    style={{
                      marginTop: 12,
                      paddingTop: 12,
                      borderTop: "1px solid var(--c-ai-lane-line)",
                    }}
                  >
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => navigate(`/audit/${project.id}`)}
                    >
                      <I.Audit size={14} /> Apri i risultati dell&apos;analisi
                    </button>
                    {/* Two sentences, and the second one is CONDITIONAL on
                        being able to prove it. `kbProgress.percent` is `null`
                        both when the Knowledge Base has nothing to do and when
                        we cannot measure it, so "la Knowledge Base continua"
                        would be a claim on a value that does not carry it. It
                        is printed only where the number itself says work is
                        outstanding. */}
                    <div style={{ fontSize: 11.5, color: "var(--c-ink-3)", marginTop: 6 }}>
                      L&apos;analisi del computo è pronta: puoi aprirla adesso.
                      {kbProgress.percent != null && kbProgress.percent < 100 && (
                        <> La Knowledge Base continua per conto suo — non serve aspettarla.</>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {/* `P-39` `L4-07` — the connector between the two lanes.

                  This is a CORRECTION. `3d3451c`'s message asserted "the lane
                  container is `gap: 0` with no connector at all". The first
                  half is right; the second is false about the file it cites.
                  The prototype has two two-lane containers and TWO connectors,
                  at `Carica Documenti Gara.dc.html` lines 204 and 250, both
                  exactly this markup — so the build rendered 2 children where
                  the prototype renders 3, and a test then pinned that absence.
                  Found by measuring the source rather than re-reading my own
                  prose about it, which is the failure `P-39` exists to correct.

                  The lanes stay `gap: 0`; the 2px padding here IS the seam, so
                  the spacing comes from the connector rather than from a gap
                  both halves would have to agree about.

                  `aria-hidden` is a DECLARED divergence from the prototype: a
                  1px rule is decoration, and an empty span would be ignored by
                  a screen reader anyway — this states the intent rather than
                  relying on that. */}
              <div
                style={{ display: "flex", justifyContent: "center", padding: "2px 0" }}
                aria-hidden="true"
                data-testid="lane-connector"
              >
                <span style={{ width: 1, height: 14, background: "var(--c-line-strong)" }} />
              </div>
              <div
                style={{
                  padding: "16px 18px",
                  background: "var(--c-brand-lane)",
                  border: "1px solid var(--c-brand-lane-line)",
                  borderRadius: 12,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  <span
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 9,
                      background: "var(--c-brand-soft)",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <I.Network size={16} strokeWidth={2} style={{ color: "var(--c-brand)" }} />
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div className="t-eyebrow" style={{ color: "var(--c-brand)" }}>
                      Processo 2
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--c-ink)" }}>
                      Knowledge Base
                    </div>
                  </div>
                  {/* The prototype heads BOTH lanes with a percentage; this one
                      was never built, and `M42` found the suite would have
                      accepted an invented one without objecting. `null` while
                      nothing has been approved yet — an honest absence, not a
                      0% that reads as "started and stuck". */}
                  {kbProgress.percent != null && (
                    <span
                      style={{
                        marginLeft: "auto",
                        fontSize: 15,
                        fontWeight: 600,
                        color: "var(--c-brand)",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {kbProgress.percent}%
                    </span>
                  )}
                </div>
                {/* `optional + lockedIn`, never `optional` alone.
                    Reading only the vetoable bucket meant a bundle whose sole
                    narrative document is the Relazione — which is locked in, not
                    optional — printed "Nessun documento proposto" here while the
                    notice a few centimetres above said "1 documento proposto".
                    Not a race: the steady state of a very common bundle. */}
                <div style={{ fontSize: 13, fontWeight: 600 }}>
                  {kbAwaiting > 0
                    ? isKbAdmin
                      ? `${kbAwaiting} ${plural(kbAwaiting, "documento in attesa", "documenti in attesa")} della tua conferma`
                      : `${kbAwaiting} ${plural(kbAwaiting, "documento in attesa", "documenti in attesa")} di conferma da un amministratore`
                    : kb.decided.length > 0
                      ? // Split from the states themselves, not from "not skipped".
                        // `DECIDED_KB_STATES` holds queued · forwarding · forwarded ·
                        // failed · skipped, and the old `!== "skipped"` reported the
                        // first, second and FOURTH as "inviati" — while the pill on the
                        // row beside it read "Invio in corso" and "Invio non riuscito"
                        // for the same documents. Six lines below, `kbReachedKb` already
                        // made the distinction, so the file knew.
                        [
                          // The two TERMINAL outcomes always print, zero included: "0
                          // esclusi" is a fact the reader wants. The two transient ones
                          // print only when they exist, because "0 non riusciti" on
                          // every healthy batch is noise that trains people to skip the
                          // line.
                          [
                            kb.decided.filter((d) => d.kbForwardState === "forwarded").length,
                            "inviati",
                            true,
                          ],
                          [
                            kb.decided.filter((d) =>
                              ["queued", "forwarding"].includes(d.kbForwardState),
                            ).length,
                            "in invio",
                            false,
                          ],
                          [
                            kb.decided.filter((d) => d.kbForwardState === "failed").length,
                            "non riusciti",
                            false,
                          ],
                          [
                            kb.decided.filter((d) => d.kbForwardState === "skipped").length,
                            "esclusi",
                            true,
                          ],
                        ]
                          .filter(([n, , always]) => always || (n as number) > 0)
                          .map(([n, label]) => `${n} ${label}`)
                          .join(" · ")
                      : "Nessun documento proposto per la Knowledge Base"}
                </div>
                {/* `P-80` — the BREAKDOWN. One percentage cannot answer the
                    question this lane is asked.

                    Omar, 12 Sep: *"Show delivery, synthesis generation,
                    checking, recovery, partial checks, awaiting review,
                    availability and failures/exclusions separately. Identify
                    active and queued work. Awaiting an administrator must not
                    look like an indefinitely running process."*

                    That last sentence names a gap `kbProgress.ts` had already
                    admitted in its own comment: a `pending-review` document
                    sits at 6/7 exactly like a finished ingest, so a document
                    blocked on a PERSON was numerically indistinguishable from
                    one almost done — and it is the state 10 of Torino's 18
                    documents are actually in.

                    Ordered by where the work IS, not by importance: queued →
                    being read → waiting for a person → available. Zero counts
                    are omitted, because «0 in coda» on every healthy batch is
                    the noise that trains people to skip the line — except the
                    ones that are decisions, which are handled by their own
                    sentence above. */}
                {kbProgress.counted > 0 && (
                  <div
                    data-testid="kb-breakdown"
                    style={{
                      fontSize: 11.5,
                      color: "var(--c-ink-3)",
                      marginTop: 4,
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "2px 10px",
                    }}
                  >
                    {[
                      [kbProgress.queued, "in coda"],
                      [kbProgress.synthesizing, "in lavorazione"],
                      [kbProgress.live, "disponibili"],
                      [kbProgress.excluded, "esclusi"],
                    ]
                      .filter(([n]) => (n as number) > 0)
                      .map(([n, label]) => (
                        <span key={label as string}>
                          {n as number} {label as string}
                        </span>
                      ))}
                    {/* Separated from the list above and given its own colour,
                        because it is the one that is NOT a process: nothing
                        will move until somebody acts. Drawing it beside «in
                        lavorazione» in the same grey is exactly what made it
                        read as an indefinitely running process. */}
                    {kbProgress.awaitingReview > 0 && (
                      <span style={{ color: "var(--c-warn)", fontWeight: 600 }}>
                        {kbProgress.awaitingReview}{" "}
                        {plural(
                          kbProgress.awaitingReview,
                          "in attesa di una decisione",
                          "in attesa di una decisione",
                        )}
                      </span>
                    )}
                    {/* `KBR-62` — a fact ABOUT documents, not a state instead of
                        one, which is why it is counted separately and subtracted
                        from nothing. Two of Torino's are `live` AND partially
                        checked. */}
                    {kbProgress.incompleteScan > 0 && (
                      <span style={{ color: "var(--c-warn)" }}>
                        {kbProgress.incompleteScan} con controllo incompleto
                      </span>
                    )}
                  </div>
                )}
                {/* A decision being honoured on a row that no longer shows the
                    control that made it. Correcting a document's TYPE can move it
                    into the required set, where it has no checkbox and sits in a
                    group Passo 2 renders collapsed — so without this line the
                    veto is invisible in both directions: the tick is gone, and
                    nothing says it is still being obeyed. */}
                {kb.vetoedNowRequired.length > 0 && (
                  <div style={{ fontSize: 11.5, color: "var(--c-warn)", marginTop: 4 }}>
                    {kb.vetoedNowRequired.length === 1
                      ? `${kb.vetoedNowRequired[0].filename} è ora richiesto dall'analisi, ma resta ESCLUSO dalla Knowledge Base come avevi scelto.`
                      : `${kb.vetoedNowRequired.length} documenti che avevi escluso sono ora richiesti dall'analisi; restano esclusi dalla Knowledge Base come avevi scelto.`}
                  </div>
                )}
                <div style={{ fontSize: 11.5, color: "var(--c-ink-3)", marginTop: 4 }}>
                  {/* `P-46` — the first TWO branches here are UNREACHABLE for the
                      same reason as the status chain above: this lane renders only
                      on `activeTab === "3"`, and tab 3 cannot coexist with
                      `awaitingStart`. Only the final `else` can be taken. Kept and
                      annotated rather than deleted, and flagged for the adversarial
                      review; the KB-authority claim itself did NOT go with them —
                      `IntakeReviewPanel.tsx:158` carries it on Passo 2, which is
                      where the decision actually is. */}
                  {awaitingStart && isKbAdmin ? (
                    <>
                      Confermi questa scelta insieme all&apos;avvio. Restano comunque due lavori
                      separati: se la Knowledge Base non parte, l&apos;analisi va avanti lo stesso.
                    </>
                  ) : awaitingStart ? (
                    /* `isKbAdmin`, not just `awaitingStart`. `member` is what every
                       ordinary user of the live product holds today and it does NOT
                       carry `kb:approve`, so `startRun` skips `kb.submit()` for them
                       — while this line sat directly under a headline correctly
                       reading "di conferma da un amministratore" and told them the
                       opposite about the very button they were about to press. */
                    <>
                      La conferma per la Knowledge Base spetta a un amministratore. L&apos;analisi
                      parte comunque: sono due lavori separati.
                    </>
                  ) : (
                    <>
                      Questa scelta non tocca l&apos;analisi: la Knowledge Base è separata e
                      l&apos;analisi va avanti comunque.
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Notices, above the thing they concern rather than below the fold in
              a 320px column. */}
          {notices.map((n, i) => (
            <div
              key={i}
              role={n.tone === "err" ? "alert" : undefined}
              style={{
                fontSize: 12,
                color: TONE[n.tone].fg,
                background: TONE[n.tone].bg,
                borderRadius: 8,
                padding: "9px 12px",
              }}
            >
              {n.node}
            </div>
          ))}

          {/* Drop zone. Two shapes, one behaviour.

              `P-37`/A5: the prototype REPLACES the big zone with a 64px strip
              the moment anything is staged, rather than showing both. That is
              not only space — the strip is where the screen says WHY it is
              frozen. Before this, an in-flight upload dimmed the zone to 0.5
              and offered no reason, so the one gesture a user would try during
              the slowest part of the screen silently did nothing.

              Both shapes carry the identical drag handlers, so nothing about
              WHERE you can drop changes with the shape. The handlers live in
              `dropHandlers` for exactly that reason: two copies would be two
              chances for one of them to drift out of the `stagingLocked`
              guard.

              `P-79` — HIDDEN IN FASE 3. Omar, 12 Sep: *"Remove also «Trascina
              altri file o cartelle qui / Sfoglia file» module."* The outcome
              screen is about what the run concluded; dropping a file there
              starts nothing and re-runs nothing, so the zone offered a gesture
              with no consequence in the one place a reader is looking for the
              result.

              The `stagedVisible.length > 0` escape is not a hedge, it is the
              only state where hiding it would STRAND someone: files staged on
              Passo 2 and never sent are still listed below, and removing the
              only control that manages them would leave a list nobody can act
              on. Adding documents after a run is «Vedi i documenti del
              progetto», which the outcome card already offers. */}
          {!hideWizard &&
            (phase === 1 && stagedVisible.length === 0 ? (
              <div
                {...dropHandlers}
                role="group"
                aria-label={DROPZONE_LABEL}
                style={{
                  border: `1.5px dashed ${drag ? "var(--c-brand)" : "var(--c-line-strong)"}`,
                  background: drag ? "var(--c-brand-soft)" : "var(--c-bg-elev)",
                  borderRadius: 12,
                  padding: "36px 24px",
                  textAlign: "center",
                  transition: "all var(--tr-fast)",
                  opacity: stagingLocked ? 0.5 : 1,
                }}
              >
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 10,
                    background: "var(--c-brand-soft)",
                    color: "var(--c-brand)",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 12,
                  }}
                >
                  <I.Upload size={22} />
                </div>
                <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 4 }}>
                  {drag ? "Rilascia per aggiungere alla gara" : "Trascina qui lo ZIP del bando"}
                </div>
                <div style={{ fontSize: 12.5, color: "var(--c-ink-3)" }}>
                  PDF · Excel · DWG · ZIP — fino a 1 GB
                </div>
                <div style={{ fontSize: 11.5, color: "var(--c-ink-3)", marginTop: 4 }}>
                  Le cartelle mantengono la loro struttura. Uno ZIP viene aperto automaticamente.
                </div>
                <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 14 }}>
                  <button className="btn btn-sm" onClick={filePicker.open} disabled={stagingLocked}>
                    Sfoglia file
                  </button>
                  <button
                    className="btn btn-sm"
                    onClick={folderPicker.open}
                    disabled={stagingLocked}
                  >
                    Sfoglia cartella
                  </button>
                </div>
                {filePicker.input}
                {folderPicker.input}
              </div>
            ) : (
              <div
                {...dropHandlers}
                role="group"
                aria-label={DROPZONE_LABEL}
                style={{
                  minHeight: 64,
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 12,
                  border: `1.5px dashed ${drag ? "var(--c-brand)" : "var(--c-line-strong)"}`,
                  background: drag ? "var(--c-brand-soft)" : "var(--c-bg-elev)",
                  borderRadius: 10,
                  padding: "10px 14px",
                  transition: "all var(--tr-fast)",
                  opacity: stagingLocked ? 0.55 : 1,
                }}
              >
                <span style={{ color: "var(--c-brand)", display: "inline-flex" }}>
                  <I.Upload size={18} />
                </span>
                <span style={{ fontSize: 12.5, color: "var(--c-ink-3)", flex: 1, minWidth: 180 }}>
                  {stagingLocked
                    ? "Caricamento in corso — non puoi aggiungere altri file adesso"
                    : drag
                      ? "Rilascia per aggiungere alla gara"
                      : "Trascina altri file o cartelle qui"}
                </span>
                <button className="btn btn-sm" onClick={filePicker.open} disabled={stagingLocked}>
                  Sfoglia file
                </button>
                <button className="btn btn-sm" onClick={folderPicker.open} disabled={stagingLocked}>
                  Sfoglia cartella
                </button>
                {filePicker.input}
                {folderPicker.input}
              </div>
            ))}

          {/* The staging list: files chosen but not yet sent. Present in EVERY
              phase where something is staged — a file dropped during a running
              analysis must be visible and sendable, not silently held in state. */}
          {/* `P-39` `F1` — the empty Fase 1 screen carried three cards that the
              prototype does not have: this one holding "Pronti da caricare (0)"
              and an empty-state sentence, the project-name card, and the sticky
              commit bar. Together ~610px of furniture in five elements against
              the prototype's ~340px in two, which pushed the drop target — the
              only thing there is to do on that screen — from ~83% of the column
              to ~39%. All three now appear only once something is staged.

              CORRECTION (audit of this same commit): this comment first said
              they "live behind the same condition the prototype uses
              (`hasStaged`)". There is no `hasStaged` identifier in this file —
              the word appeared exactly once, in that sentence — and the three
              conditions are three DIFFERENT expressions, which is what strands
              the two branches noted at the commit bar below:

                staging list  `stagedVisible.length > 0`
                project name  `phase === 1 && stagedVisible.length > 0`
                commit bar    `stagedPending > 0`

              Naming a mechanism is a claim; a reader trusting that one would
              have believed those branches reachable. */}
          {stagedVisible.length > 0 && (
            <div className="card" style={{ overflow: "hidden" }}>
              <div
                style={{
                  padding: "12px 16px",
                  borderBottom: "1px solid var(--c-line)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 600 }}>
                  Pronti da caricare ({stagedVisible.length})
                </div>
                <span className="muted" style={{ fontSize: 12 }}>
                  {fmtTotalSize(stagedVisible)}
                </span>
              </div>
              <div>
                {/* The empty-state sentence that stood here is deleted, not
                    hidden: the card above cannot mount at zero rows any more,
                    so the branch was unreachable. Its content (which document
                    types to drop) is not lost — it is on the drop zone itself,
                    which is now the whole screen. No test asserted it. */}
                {stagedVisible.map((s, i) => {
                  const disp = fileToUploadFile(s.file);
                  const archive = isArchive(s.file);
                  const Ico = archive ? I.Box : (FILE_ICON[disp.type] ?? I.File);
                  return (
                    <div
                      key={s.key}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        padding: "10px 16px",
                        borderBottom:
                          i < stagedVisible.length - 1 ? "1px solid var(--c-line)" : "none",
                      }}
                    >
                      <span
                        style={{
                          width: 34,
                          height: 34,
                          flex: "none",
                          borderRadius: 8,
                          background: "var(--c-brand-soft)",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Ico size={16} strokeWidth={2} style={{ color: "var(--c-brand)" }} />
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 500,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {s.file.name}
                        </div>
                        <div style={{ fontSize: 11.5, color: "var(--c-ink-3)", marginTop: 1 }}>
                          {s.folderPath && (
                            <span title={s.folderPath}>
                              <I.Folder size={11} style={{ verticalAlign: -1 }} /> {s.folderPath}{" "}
                              ·{" "}
                            </span>
                          )}
                          {disp.size}
                          {s.state === "uploaded" && (
                            <span style={{ color: "var(--c-ok)" }}> · caricato ✓</span>
                          )}
                          {/* `P-41` — two phases, named honestly. While bytes are
                              leaving, the percentage is REAL (XHR progress).
                              Once the body is fully sent the server is opening
                              and classifying, whose duration we cannot measure
                              — so the line changes subject instead of showing a
                              number that has stopped meaning anything. Before
                              the first progress event (or if none ever fires)
                              the copy degrades to exactly what it said before
                              this feature existed. */}
                          {s.state === "uploading" &&
                            (() => {
                              /* ONE predicate with the bar below, not two.
                                 `P-48` round 2: the label used to branch on
                                 `uploadPct >= 100` while the bar branched on
                                 `key in importCounts`. They are meant to track
                                 the same handover from bytes to documents, and
                                 `onUploadProgress` only fires when the browser
                                 reports a computable length -- so a body whose
                                 length it will not compute left the label saying
                                 «caricamento…» over a bar that was already
                                 importing. Both now read `archiveRowProgress`. */
                              const bar = archiveRowProgress(
                                uploadPct[s.key],
                                s.key in importCounts ? importCounts[s.key] : undefined,
                              );
                              const counts = importCounts[s.key];
                              let text: string;
                              if (bar.phase === "sending") {
                                /* Still bytes. `uploadPct` is exactly the right
                                   signal INSIDE this phase — it is what "how
                                   much has left the machine" means — and at 100
                                   it means the server has it and is working,
                                   which each shape names differently. */
                                if (uploadPct[s.key] === undefined) {
                                  /* Before the first progress event NOTHING has
                                     measurably left the machine, so the copy is
                                     the neutral one for BOTH shapes — the
                                     designer caught the archive branch claiming
                                     «apertura e riconoscimento…» here, the same
                                     string that means "fully sent, server
                                     working", in a state where neither half was
                                     true. */
                                  text = "caricamento…";
                                } else if (uploadPct[s.key] >= 100) {
                                  text = archive ? "apertura e riconoscimento…" : "elaborazione…";
                                } else {
                                  text = `caricamento… ${uploadPct[s.key]}%`;
                                }
                              } else if (!archive) {
                                text = "elaborazione…";
                              } else if (bar.phase === "saving") {
                                /* Every member is recognised and the server is
                                   writing the rows. Said out loud rather than
                                   left as a full bar: the import can still fail
                                   here and discard all of it, and «N di N» over
                                   a complete-looking bar is the claim that
                                   cannot be walked back. */
                                text = `riconosciuti ${counts?.total ?? 0} documenti… salvataggio`;
                              } else if (counts && counts.total > 0) {
                                /* `total > 0`, matching `archiveRowProgress`'s own
                                   guard. The old check tested the OBJECT's
                                   truthiness, and `{imported: 0, total: 0}` is
                                   truthy — so it printed «0 di 0 documenti», the
                                   exact sentence that guard exists to prevent. */
                                text = `apertura e riconoscimento… ${counts.imported} di ${counts.total} documenti`;
                              } else {
                                text = "apertura e riconoscimento…";
                              }
                              return <span> · {text}</span>;
                            })()}
                          {s.state === "failed" && (
                            <span style={{ color: "var(--c-err)" }}> · non caricato</span>
                          )}
                        </div>
                        {/* `P-41` — the bar under the row, the prototype's own
                            shape: 4px track on the sunken tone, brand fill,
                            width driven by real bytes. Rendered only while
                            uploading AND only once a progress event has
                            arrived: a bar pinned at 0 with no event stream
                            behind it would be the frozen screen this feature
                            exists to remove, wearing a new costume. */}
                        {s.state === "uploading" &&
                          (uploadPct[s.key] !== undefined || s.key in importCounts) &&
                          (() => {
                            /* `P-48` — the bar now has two subjects, and the
                               accessible NAME says which one, because that is
                               the only part of it a test can read: jsdom lays
                               nothing out, so «is the fill the right width» is
                               unassertable while «what is this bar measuring»
                               is exactly assertable. Two bars sharing one label
                               would be a discriminator that stops
                               discriminating the moment the phase changes. */
                            const bar = archiveRowProgress(
                              uploadPct[s.key],
                              s.key in importCounts ? importCounts[s.key] : undefined,
                            );
                            const label =
                              bar.phase === "sending"
                                ? `Caricamento di ${s.file.name}`
                                : `Importazione di ${s.file.name}`;
                            return (
                              <div
                                role="progressbar"
                                aria-valuenow={bar.value}
                                aria-valuemin={0}
                                aria-valuemax={100}
                                aria-label={label}
                                style={{
                                  height: 4,
                                  background: "var(--c-bg-sunken)",
                                  borderRadius: 2,
                                  overflow: "hidden",
                                  marginTop: 6,
                                }}
                              >
                                <div
                                  style={{
                                    height: "100%",
                                    borderRadius: 2,
                                    /* A different token for the second phase, so
                                       the restart reads as a new subject rather
                                       than as the bar losing its place. */
                                    background:
                                      bar.phase === "sending" ? "var(--c-brand)" : "var(--c-ok)",
                                    width: `${bar.value}%`,
                                    transition: "width .15s linear",
                                  }}
                                />
                              </div>
                            );
                          })()}
                        {archive && (
                          <div style={{ fontSize: 11, color: "var(--c-ink-3)", marginTop: 2 }}>
                            Archivio — lo apriamo noi e riconosciamo ogni documento
                            all&apos;interno.
                          </div>
                        )}
                      </div>
                      {/* A ZIP gets no role select: `uploadArchive` posts only the
                          file, so the control was inert — and it defaulted to
                          "Altro", inviting a correction that did nothing. */}
                      {!archive && (
                        <select
                          className="input"
                          value={s.role}
                          onChange={(e) => setRole(s.key, e.target.value as TenderDocRole)}
                          disabled={stagingLocked || s.state === "uploaded"}
                          style={{ width: 190, fontSize: 12, padding: "4px 8px" }}
                          aria-label={`Ruolo di ${s.key}`}
                        >
                          {ROLE_OPTIONS.map((r) => (
                            <option key={r} value={r}>
                              {DOC_ROLE_LABEL[r]}
                            </option>
                          ))}
                        </select>
                      )}
                      <button
                        className="btn btn-ghost btn-icon btn-sm"
                        disabled={stagingLocked || s.state === "uploaded"}
                        aria-label={`Rimuovi ${s.file.name} dalla lista`}
                        onClick={() => setStaged((prev) => prev.filter((x) => x.key !== s.key))}
                      >
                        <I.X size={13} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* `P-37`/B1 + C1 — the step strip, which is also the navigation.

              It replaces two dots and the words "Passo 1 di 2". Those were
              accurate and useless: they described a position without offering
              to change it, so on Passo 2 the only route back was a button at
              the very bottom of a long panel, and nothing on screen said Passo
              1 still existed. QA reported losing the way back three times.

              Once a run exists the strip grows a THIRD tab, and the two step
              tabs become read-only views of what was confirmed — the prototype's
              `viewTab: '3'`, which is the discovery that the running screen is
              a panel in this card rather than a different screen.

              One deliberate divergence from the prototype: it DISABLES Passo 2
              while a required document is missing (`canGo2 = missing.length ===
              0`). We do not, and must not. That check does not exist on this
              client — `P-31`'s last round deleted the client-side prediction of
              which documents are missing precisely because keeping it in sync
              failed three times running, and the server's 422 is the authority.
              A disabled tab here would be that prediction coming back, wearing
              a different hat, in the one place where being wrong makes a paid
              run unreachable. */}
          {showTabs && (
            <div className="card weld-above" style={{ overflow: "hidden", padding: 0 }}>
              <div
                role="tablist"
                aria-label="Passaggi del progetto"
                style={{
                  display: "grid",
                  gridTemplateColumns: awaitingStart ? "1fr 1fr" : "1fr 1fr 1.15fr",
                  background: "var(--c-bg-sunken)",
                  borderBottom: "1px solid var(--c-line)",
                }}
              >
                {tabIds.map((id, idx) => {
                  const on = activeTab === id;
                  const isProgressTab = id === "3";
                  const kicker =
                    id === "1"
                      ? "Passo 1"
                      : id === "2"
                        ? "Passo 2"
                        : phase === 3
                          ? "Esito"
                          : /* NOT "In corso", which the analysis track card
                               already prints as its status. The same two words
                               twice on one screen, from two different sources,
                               is a reader's problem before it is a test's. */
                            "Avanzamento";
                  const title =
                    id === "1"
                      ? /* "Computo" in BOTH modes. The prototype widens this to
                           "Analisi del computo" before a start, which is the
                           exact string the track card beside it already uses as
                           its eyebrow — one screen, two elements, same words,
                           different meanings. */
                        "Computo"
                      : id === "2"
                        ? "Knowledge Base"
                        : phase === 3
                          ? /* `P-79` — this read «Completato» / «Non riuscita»,
                               unqualified, and that was the FALSE CLAIM on
                               Omar's screenshot: `phase` is derived from
                               `job.status` ALONE, with zero Knowledge Base
                               input, so it said «Completato» while Processo 2
                               was still running.
      
                               Naming the object is the fix, not deleting the
                               label — the tab is the route to the outcome and
                               still has to say so. «Analisi completata» is a
                               claim about the analysis and nothing else, which
                               is the only track this value knows about. In the
                               state where nothing is left to confirm the strip
                               is gone entirely and the question does not
                               arise. */
                            analysisFailed
                            ? "Analisi non riuscita"
                            : "Analisi completata"
                          : "Computo e Knowledge Base";
                  /* The badge asserts an OUTCOME, so it may only claim one where
                     one exists. On the two step tabs after a start, that outcome
                     is "you confirmed this", which is true whatever the run then
                     did — the tick is about the CONFIRMATION, not the analysis.
                     Tab 3 is the only one that reports the run, and it is the
                     only one that can turn red. */
                  /* A tick is a claim, and colour is a claim. QA found tab 2
                     printing a green ✓ on a run whose kb-forward had thrown —
                     the code's justification being that the tick reports the
                     CONFIRMATION, not the outcome. True of the word and not of
                     the colour: nothing else on that tab distinguishes "sent"
                     from "refused", and no assertion in 732 tests was looking
                     at either. When the batch failed, say so on the tab. */
                  const stepBadge = isProgressTab
                    ? null
                    : stepTabBadge({
                        id: id === "2" ? "2" : "1",
                        kbError: !!kb.error,
                        kbAwaiting,
                        kbFailed,
                        kbDecided: kb.decided.length,
                        missingRequired: stillMissing.length,
                      });
                  const badge = isProgressTab
                    ? phase === 3
                      ? analysisFailed
                        ? "!"
                        : "✓"
                      : `${progress ?? 0}%`
                    : awaitingStart
                      ? id
                      : (stepBadge?.badge ?? "✓");
                  const badgeTone = isProgressTab
                    ? phase === 3
                      ? analysisFailed
                        ? "var(--c-err)"
                        : "var(--c-ok)"
                      : "var(--c-ai)"
                    : awaitingStart
                      ? on
                        ? "var(--c-brand)"
                        : "var(--c-line-strong)"
                      : (stepBadge?.tone ?? "var(--c-ok)");
                  const filled = isProgressTab
                    ? true
                    : awaitingStart
                      ? on
                      : (stepBadge?.filled ?? true);
                  return (
                    <button
                      key={id}
                      id={`intake-tab-${id}`}
                      type="button"
                      role="tab"
                      aria-selected={on}
                      aria-controls="intake-tabpanel"
                      tabIndex={on ? 0 : -1}
                      disabled={tabsFrozen && !on}
                      onClick={() => pickTab(id)}
                      onKeyDown={(e) => tabKeyDown(e, id)}
                      style={{
                        font: "inherit",
                        textAlign: "left",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        padding: "12px 16px",
                        border: "none",
                        borderLeft: idx === 0 ? "none" : "1px solid var(--c-line)",
                        boxShadow: on ? `inset 0 -3px 0 ${badgeTone}` : "none",
                        background: on ? "var(--c-bg-elev)" : "transparent",
                        opacity: tabsFrozen && !on ? 0.5 : 1,
                      }}
                    >
                      <span
                        aria-hidden
                        style={{
                          minWidth: 26,
                          height: 26,
                          padding: "0 4px",
                          borderRadius: 13,
                          background: filled ? badgeTone : "var(--c-bg-elev)",
                          border: `1.5px solid ${badgeTone}`,
                          color: filled ? "#fff" : "var(--c-ink-3)",
                          fontSize: badge.length > 2 ? 9.5 : 12,
                          fontWeight: 600,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {badge}
                      </span>
                      <span style={{ minWidth: 0 }}>
                        <span
                          className="t-eyebrow"
                          style={{
                            display: "block",
                            color: on ? "var(--c-ink)" : "var(--c-ink-3)",
                          }}
                        >
                          {kicker}
                        </span>
                        <span
                          style={{
                            display: "block",
                            fontSize: 12.5,
                            fontWeight: 600,
                            marginTop: 1,
                            color: on ? "var(--c-ink)" : "var(--c-ink-3)",
                          }}
                        >
                          {title}
                        </span>
                        {/* No elapsed time here. It is already on the track
                            card, in the Avanzamento panel and in the sidebar —
                            ORD-03's probe found FOUR surfaces printing
                            "trascorso" on one screen. The badge carries the
                            percentage, which is the thing a tab label can
                            usefully hold. */}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* What the server actually found, once it has told us.
              `P-43` (Omar, 2026-08-22): each step now renders ONLY its own
              groups — Passo 1 the four required documents, Passo 2 the
              Knowledge Base set and the collapsed attachments, exactly the
              prototype's two lists. The worry that used to justify rendering
              everything — "the remedy would point at rows not on screen" — is
              met inside the missing-role row itself: its assignment select
              lists every document in the project, and `P-43` added a direct
              upload beside it. */}
          {panelMounted && (
            <div
              className={showTabs ? "weld-below" : undefined}
              role="tabpanel"
              id="intake-tabpanel"
              aria-labelledby={`intake-tab-${activeTab}`}
            >
              <IntakeReviewPanel
                documents={documents}
                kb={kb}
                correction={correction}
                download={download}
                onlyReview={onlyReview}
                setOnlyReview={setOnlyReview}
                canDecide={isKbAdmin}
                /* The strip above now says which step this is, so the panel
                   follows the STRIP rather than the phase. Before the strip
                   there was nothing to follow: after a start it fell back to
                   the undivided "all" view, which is why a finished run showed
                   the two steps merged into one list with no way to tell which
                   half you had confirmed. */
                view={activeTab === "2" ? "kb" : "required"}
                analysisState={
                  analysisDone || analysisFailed ? "finished" : jobId ? "running" : "not_started"
                }
                missingUpload={{
                  start: startMissingUpload,
                  activeRole: missingUploadState?.role ?? null,
                  activeName: missingUploadState?.name ?? null,
                  pct: missingUploadState?.pct ?? null,
                  errorRole: missingUploadError?.role ?? null,
                  error: missingUploadError?.message ?? null,
                }}
                priceLists={{
                  // `isSuccess`, never `.data ?? []` — see PriceListsPanel.ready.
                  ready: priceListsQuery.isSuccess,
                  editions: priceListsQuery.data ?? [],
                  remove: (editionId) => {
                    if (!project || removingRef.current.has(editionId)) return;
                    removingRef.current.add(editionId);
                    removePriceList.mutate(
                      { projectId: project.id, editionId },
                      { onSettled: () => removingRef.current.delete(editionId) },
                    );
                  },
                  removeDoc: (doc) => {
                    if (!canDeleteDocument) {
                      setRefusedDocDelete(doc.filename);
                      return;
                    }
                    if (project) deleteDocument.mutate({ id: doc.id, projectId: project.id });
                  },
                  removeBusy: removePriceList.isPending || deleteDocument.isPending,
                  download: priceListDownload,
                  // `isSuccess`, never `.data ?? null` — an errored library
                  // query would otherwise be indistinguishable from an empty
                  // one, and the picker would tell the user the library holds
                  // nothing. Same rule as `ready` above.
                  library: priceListLibraryQuery.isSuccess ? priceListLibraryQuery.data : null,
                  add: (editionId) => {
                    if (!project || addingRef.current.has(editionId)) return;
                    // Same latch as `remove`: a mutation whose onSuccess
                    // invalidates the cache it reads must be serialised, and
                    // react-query's `scope` queues rather than guards.
                    addingRef.current.add(editionId);
                    addPriceList.mutate(
                      { projectId: project.id, editionId },
                      { onSettled: () => addingRef.current.delete(editionId) },
                    );
                  },
                  addBusy: addPriceList.isPending,
                  upload: startListinoUpload,
                  uploading: listinoUpload,
                  error:
                    listinoError ??
                    (removePriceList.isError
                      ? // The endpoint's `detail` is English and internal
                        // («price list not associated»); the card speaks to a
                        // person, in Italian, about what to do next.
                        //
                        // Through the SAME mapper as the add arm below. This
                        // used to be a fixed string ending «Ricarica la pagina
                        // e riprova», which on a 403 told a Reviewer to do the
                        // one thing that cannot help them — their role refuses
                        // them again after every reload.
                        priceListMessage(removePriceList.error, "remove")
                      : addPriceList.isError
                        ? // `P-47b`. This arm was MISSING, and its absence was
                          // silent by construction: the `<select>` resets to its
                          // placeholder on every change, so a refused pick looks
                          // exactly like a pick that has not happened yet. A 409
                          // (the edition lost its last workbook), a 403 (the
                          // caller lacks `document:write`) and a 500 all left the
                          // user with no row and no reason.
                          //
                          // The server's `detail` is English and internal, so the
                          // 409 gets its own Italian sentence naming what to do;
                          // anything else falls through to the shared mapper.
                          addPriceListMessage(addPriceList.error)
                        : deleteDocument.isError
                          ? apiErrorMessage(deleteDocument.error)
                          : null),
                  // `P-47b` — the client lock must agree with the SERVER lock,
                  // and it did not.
                  //
                  // `_refuse_if_run_exists` deliberately excludes `failed`, with
                  // a paragraph of reasoning: a failed run is exactly when
                  // someone needs to change the listino and try again, and
                  // refusing would leave the project with no exit. This line
                  // still read `|| analysisFailed`, and all four controls (both
                  // Rimuovi buttons, the .xlsx upload, the library picker) hang
                  // off `!pl.locked` — so the server allowed the change and the
                  // screen hid every control that could make it. The exit was
                  // inert: pay, fail, «Riprova analisi» with the identical
                  // inputs, fail again.
                  //
                  // A guarantee is not a property of the system until a consumer
                  // honours it, and here the consumer contradicted it. Found by
                  // the adversarial review, in the round that added the lock.
                  locked: (Boolean(jobId) || analysisDone) && !analysisFailed,
                }}
              />
            </div>
          )}

          {/* `P-37`/C1 — the Avanzamento panel. The third tab's content, and in
              Fase 3 the outcome card below stands in for it.

              The analysis track reports a real percentage because the server
              sends one. The Knowledge Base track reports a STATE and no number:
              it has no clock until `KB-11`, and the prototype's second
              percentage is a drawing. A progress bar that advances on nothing is
              the most confident lie a screen can tell. */}
          {phase === 2 && !awaitingStart && activeTab === "3" && (
            <div
              role="tabpanel"
              id="intake-tabpanel"
              aria-labelledby="intake-tab-3"
              className="card"
              style={{ padding: 22 }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <span
                  aria-hidden
                  style={{
                    width: 9,
                    height: 9,
                    borderRadius: "50%",
                    background: "var(--c-ai)",
                    boxShadow: "0 0 0 4px var(--c-ai-soft)",
                    flexShrink: 0,
                  }}
                />
                {/* "in parallelo" only when something really is running beside
                    the analysis. */}
                <div style={{ fontSize: 13.5, fontWeight: 600 }}>
                  {kbReachedKb ? "Al lavoro, in parallelo." : "Al lavoro."}
                </div>
                {/* The track card directly above prints "N% · trascorso X ·
                    stimate 1–3 ore" already. */}
              </div>
              {/* This was "Computo e Knowledge Base procedono insieme. L'esito ti
                  aspetta quando sono pronti entrambi." — false in both halves,
                  and the file refutes it 140 lines down: `phase` is derived from
                  `job.status` alone, with zero Knowledge Base input, and the
                  outcome card it produces prints "Restano N documenti in attesa
                  di conferma" on the same screen as the esito. The second half
                  was worse for a `member`, for whom nothing was ever sent: the
                  card directly above this sentence says so, and this said they
                  were proceeding together.

                  It is the only sentence on this tab that describes what the
                  reader is waiting for, in front of a 1–3 hour paid wait. */}
              <div style={{ fontSize: 11.5, color: "var(--c-ink-3)", marginTop: 4 }}>
                L&apos;analisi del computo è in corso: l&apos;esito compare qui appena è pronta e
                non aspetta nient&apos;altro.
                {kbAwaiting > 0
                  ? isKbAdmin
                    ? ` La Knowledge Base è separata e non è ancora partita: ${kbAwaiting} ${plural(kbAwaiting, "documento aspetta", "documenti aspettano")} la tua conferma nel Passo 2.`
                    : ` La Knowledge Base è separata e non è ancora partita: ${kbAwaiting} ${plural(kbAwaiting, "documento aspetta", "documenti aspettano")} la conferma di un amministratore.`
                  : kbFailed > 0
                    ? /* The third state, and the one the sentence used to skip.
                         With every forward failed server-side, `kbAwaiting` is 0
                         and `kbReachedKb` is false, so both branches above miss
                         and the only sentence describing what the reader is
                         waiting for said nothing at all — while the track card
                         beside it printed "N non riusciti". */
                      ` La Knowledge Base non è riuscita: ${kbFailed} ${plural(kbFailed, "documento non è stato inviato", "documenti non sono stati inviati")}. L'analisi del computo non è toccata.`
                    : kbReachedKb
                      ? " La Knowledge Base procede per conto suo: l'esito dell'analisi non la aspetta."
                      : ""}
              </div>

              {/* The pipeline rail. The two PROCESS cards that used to sit here
                  were deleted in the same pass that made the track cards above
                  follow this tab — they were the same two facts, drawn twice,
                  forty pixels apart. What is left is the one thing the cards do
                  not say: which of the four stages the analysis is on.

                  No second progress bar for the Knowledge Base. The prototype
                  draws one; it has no clock behind it until `KB-11`, and a bar
                  that advances because the render advanced is the most
                  confident lie a screen can tell. */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: "7px 18px", marginTop: 16 }}>
                {PIPELINE_STEPS.map(([t], i) => {
                  const done = activeStep > i;
                  return (
                    <span key={t} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                      <span
                        aria-hidden
                        style={{
                          width: 17,
                          height: 17,
                          borderRadius: "50%",
                          border: `1px solid ${done ? "var(--c-ok)" : "var(--c-line-strong)"}`,
                          color: done ? "var(--c-ok)" : "var(--c-ink-3)",
                          fontSize: 9,
                          fontWeight: 600,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        {done ? "✓" : i + 1}
                      </span>
                      <span
                        style={{
                          fontSize: 11.5,
                          whiteSpace: "nowrap",
                          color: done ? "var(--c-ink-2)" : "var(--c-ink-3)",
                        }}
                      >
                        {t}
                      </span>
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* FASE 3 — the outcome, and where to go from here. Tab 3's panel:
              in Fase 3 the third tab reads "Esito", and this is what it shows.
              Reachable from either step tab in one click, which is the point of
              the strip — before it, going back to check a document meant
              leaving the outcome behind with no route to it. */}
          {phase === 3 && activeTab === "3" && (
            <div
              className="card"
              role="tabpanel"
              id="intake-tabpanel"
              aria-labelledby="intake-tab-3"
              style={{
                padding: 18,
                borderLeft: `3px solid ${analysisFailed ? "var(--c-err)" : TONE[verdict?.tone ?? "info"].fg}`,
              }}
            >
              <div
                style={{
                  fontSize: 15,
                  fontWeight: 600,
                  color: analysisFailed ? "var(--c-err)" : TONE[verdict?.tone ?? "info"].fg,
                }}
              >
                {analysisFailed
                  ? failureLine("Analisi non riuscita", job?.error, "")
                  : verdict?.title}
              </div>
              {/* `P-37`/C5 — the first thing anyone wants to know after a
                  failure is whether they have to upload thirty files again.
                  The screen used to answer that only by implication, via a
                  «Riprova» button further down; the answer is now in words,
                  above the button, because a person deciding whether to walk
                  away reads the sentence and not the control. */}
              {analysisFailed && (
                <div style={{ fontSize: 12.5, color: "var(--c-ink-2)", marginTop: 5 }}>
                  {/* Goal 55B — «L'estrazione si è interrotta» is said only of an ordinary
                      failure. Under «Analisi non salvata: …» the extraction FINISHED and the
                      result was not saved, and «Analisi fermata …» already states why it
                      stopped, the items read and the spend: for both the line keeps only what
                      is true in every case (`lib/jobFailure.ts`, the same rule as the title). */}
                  {isSelfContainedFailure(job?.error) ? null : (
                    <>L&apos;estrazione si è interrotta prima della fine. </>
                  )}
                  <strong>I documenti restano caricati</strong>: puoi riprovare l&apos;analisi da
                  qui, senza ricaricare niente.
                </div>
              )}
              {!analysisFailed && verdict && (
                <div style={{ fontSize: 12.5, color: "var(--c-ink-2)", marginTop: 5 }}>
                  {verdict.body}
                </div>
              )}
              {kbAwaiting > 0 && (
                <div style={{ fontSize: 12, color: "var(--c-ink-3)", marginTop: 10 }}>
                  {/* "Puoi decidere ora qui sopra" was told to everyone, roughly ten
                      centimetres below the panel's own "Solo un amministratore può
                      confermare questa scelta". And the plural was hardcoded in a
                      file that calls `plural()` fifteen times. */}
                  {/* R4-04 — the SAME expression as the track card a few
                      centimetres above (`optional + lockedIn`) and as the commit
                      button's own `(N)`. This counted `optional` alone, so one
                      fixture printed "3 documenti in attesa della tua conferma"
                      and "Restano 2 documenti in attesa di conferma" on one
                      screen, over a button reading (3). Adding `lockedIn` to the
                      card was itself a recorded fix; its sibling never got it. */}
                  {kbAwaiting === 1
                    ? "Resta 1 documento in attesa di conferma per la Knowledge Base."
                    : `Restano ${kbAwaiting} documenti in attesa di conferma per la Knowledge Base.`}{" "}
                  {/* R5 D-4 — names the TAB, not a direction. This card is
                      tab-3-only and the panel holding that control is gated
                      `activeTab !== "3"`, so "qui sopra" pointed at a control
                      that is by construction on a different tab. Third instance
                      of this class on this screen; the KB-error notice already
                      does it the right way. */}
                  {isKbAdmin
                    ? "Puoi decidere dal Passo 2, oppure più avanti dalla pagina dei documenti."
                    : "La scelta spetta a un amministratore del tuo studio."}
                </div>
              )}
              <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
                {analysisFailed ? (
                  <>
                    <button
                      className="btn btn-primary"
                      onClick={() => void startRun(false)}
                      // A failed run is retried from phase 3, where staging is
                      // still possible: adding the errata the run choked on and
                      // pressing Riprova without sending it would repeat the run
                      // with the same documents. The commit bar renders on
                      // `stagedPending > 0` in EVERY phase, so the exit is
                      // co-present with the refusal.
                      disabled={startBlocked || startAnalysis.isPending}
                    >
                      <I.Refresh size={14} />{" "}
                      {startAnalysis.isPending ? "Avvio in corso…" : "Riprova analisi"}
                    </button>
                    {/* A disabled control with no sentence beside it is a refusal
                      with no exit — the failure this screen has already been
                      burned by twice. The commit bar carrying «Carica documenti»
                      is on screen, and this names it.

                      It used to say the bar was "above", and to point there with
                      "qui sopra". Both are wrong: the bar opens 174 lines BELOW
                      this card in the one flex column that lays the page out
                      (`order:` appears nowhere in this file), and it is
                      `position: sticky; bottom: 0`, which pins it to the bottom
                      of the viewport and never above. The identical sentence in
                      the wizard footer really does sit below the bar, which is
                      where the wording was copied from. */}
                    {/* Every blocked value, including "role". It first covered
                        only the two file cases, on the ground that a Reviewer is
                        told about their role elsewhere — but "elsewhere" is the
                        wizard footer, which phase 3 does not render, so that
                        caller got a dead button and no sentence at all. */}
                    {startBlock !== null ? (
                      <div style={{ fontSize: 11.5, color: "var(--c-ink-3)", alignSelf: "center" }}>
                        {startBlock === "role"
                          ? "Il tuo ruolo non permette di avviare un'analisi. Chiedi a un amministratore o a un editor del tuo studio."
                          : startBlock === "uploading"
                            ? "Stiamo ancora caricando i file: l'analisi riparte quando sono arrivati."
                            : startBlock === "side-upload"
                              ? sideUploadSentence(listinoUpload, missingUploadState)
                              : `${stagedPending} ${plural(stagedPending, "file non è ancora stato caricato", "file non sono ancora stati caricati")}: ${plural(stagedPending, "caricalo", "caricali")} con «Carica documenti» prima di riprovare.`}
                      </div>
                    ) : null}
                  </>
                ) : (
                  <button
                    className="btn btn-primary"
                    onClick={() => project && navigate(`/audit/${project.id}`)}
                  >
                    <I.Audit size={14} /> Apri l&apos;audit del progetto
                  </button>
                )}
                <button
                  className="btn"
                  onClick={() => project && navigate(`/projects/${project.id}/documents`)}
                >
                  <I.Folder size={14} /> Vedi i documenti del progetto
                </button>
                {/* Only when something of this project's actually reached the KB —
                    otherwise this points at a page with nothing on it, which is
                    the "never claim it was added" failure in navigational form. */}
                {kbReachedKb && (
                  <button className="btn" onClick={() => navigate("/kb")}>
                    <I.Layers size={14} /> Vai alla Knowledge Base
                  </button>
                )}
                {/* `P-79` — «Nuovo progetto» was removed here on Omar's
                    instruction of 12 Sep. It sat last in a row of three
                    destinations that are all ABOUT the run that just finished,
                    and offered instead to walk away from it — the one control
                    on the outcome card that discards the reader's context. The
                    route is unchanged and unaffected: «Torna al workspace» at
                    the top of this screen, and the workspace's own «Nuovo
                    progetto». */}
              </div>
            </div>
          )}

          {/* The one commit point, at the end of the column it concerns.
              Rendered whenever anything is waiting to be sent — in phase 1 that
              is the whole bundle, in phase 2 it is the errata someone just
              dropped. Making it phase-1-only left files strandable on screen with
              no control able to send them. */}
          {/* The project name, BELOW the file list.
              `P-37`/A1: the prototype asks for it after the drop, which is the
              order of the actual gesture — you drop the ZIP, then you name what
              you dropped. It sat above the drop zone, so the first thing the
              screen demanded was a decision about something that did not exist
              yet.

              `P-37`/A2: Città and Superficie are GONE from this form. Omar, 20
              Aug: they are to be extracted from the documents and shown on Audit
              & Discrepanze (`P-38`). Until that ships a new project stores
              neither, so `SUPERFICIE` and `COSTO / M²` render `—` there — which
              they already do for every project today. */}
          {phase === 1 && stagedVisible.length > 0 && (
            <div className="card" style={{ padding: 18 }}>
              {/* `P-39` `F18`/`T9` — `display:block` is load-bearing and was
                  missing: a `<label>` is inline and the `.input` beside it is
                  inline-block, so `marginTop` broke nothing and the two
                  rendered SIDE BY SIDE on one baseline, the caption reading as
                  a prefix to the box rather than a heading over it. The weight
                  and ink also drifted a step light (`500`/`--c-ink-2` against
                  the prototype's `600` on inherited ink), which made a required
                  field look like a hint. */}
              <label
                style={{
                  display: "block",
                  fontSize: 12,
                  fontWeight: 600,
                  color: "var(--c-ink)",
                  marginBottom: 6,
                }}
                htmlFor="project-name"
              >
                Nome del progetto
              </label>
              <input
                id="project-name"
                className="input"
                value={name}
                placeholder="Es. Riqualificazione Porta Nuova"
                onChange={(e) => setName(e.target.value)}
                disabled={!!project}
                /* `P-39` `F18` — full width of its card, as the prototype has
                   it. `maxWidth: 460` capped the field at ~70% of a ~650px
                   card, so the one input on the screen looked optional. */
                style={{ width: "100%", height: 36 }}
              />
              {!!project && (
                <div style={{ fontSize: 11.5, color: "var(--c-ink-3)", marginTop: 10 }}>
                  Il nome non si modifica da questa schermata.
                </div>
              )}
            </div>
          )}

          {/* `P-39` `F1` — `stagedPending`, not `phase === 1`: a FAILED file
              still counts as pending (`state !== "uploaded"`), so the retry
              path keeps its bar. */}
          {stagedPending > 0 && (
            <div
              className="card"
              style={{
                padding: "12px 16px",
                position: "sticky",
                bottom: 0,
                boxShadow: "var(--sh-2)",
                display: "flex",
                alignItems: "center",
                gap: 14,
                flexWrap: "wrap",
              }}
            >
              {/* UNREACHABLE TODAY, and kept deliberately. The gate on this
                  bar is `stagedPending > 0`, so the `stagedPending === 0`
                  disjunct on the button below and the "Aggiungi almeno un file"
                  branch beside it can no longer fire — the outer gate answers
                  first. Both stay as the last line against a future relaxation
                  of that gate, but nothing can reach them, so nothing can test
                  them: that is precisely how a defence-in-depth guard decays
                  into a comment. Said out loud rather than left for the next
                  reader to find. Surfaced by the audit of `609346c`.
                                `stagedPending`, never `staged.length`. Once every file is
                  uploaded there is nothing to commit, and the button read
                  "Carica e leggi i 0 file" while remaining enabled — one press
                  from a second paid 1–3 hour run. */}
              {/* `P-48` round 4 — an import is already running for this studio.
                  This is the reload surface: closing the tab or refreshing used
                  to leave a blank «Nuovo progetto» here while the server kept
                  importing, and re-dropping the ZIP created a second project
                  holding a second copy of the same tender. The banner both
                  reports the import and disables the button that would start
                  the duplicate. */}
              {foreignImport && (
                <div
                  role="status"
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 8,
                    border: "1px solid var(--c-border)",
                    background: "var(--c-bg-sunken)",
                    fontSize: 12.5,
                    color: "var(--c-ink-2)",
                    marginBottom: 12,
                  }}
                >
                  <strong style={{ fontWeight: 600 }}>
                    Un&apos;importazione è già in corso
                    {foreignImportCounts?.filename ? ` — ${foreignImportCounts.filename}` : ""}.
                  </strong>{" "}
                  {foreignImportCounts && foreignImportCounts.total > 0
                    ? `Aperti e riconosciuti ${foreignImportCounts.imported} di ${foreignImportCounts.total} documenti.`
                    : "Stiamo aprendo l'archivio."}{" "}
                  {/* Says what to DO, because the alternative reading — «it
                      failed, send it again» — is the one that creates the
                      duplicate project this banner exists to prevent. */}
                  Attendi che finisca: i documenti compariranno da soli.
                </div>
              )}
              {/* `P-39` `F8` — the commit tier. This button starts an upload of
                  up to 1 GB and is the gateway to a 1–3 hour paid run; it was
                  rendering at `.btn`'s 32px/13px/500, the same weight as a
                  toolbar affordance. `.btn-commit` is additive — `.btn` is used
                  in 22 files and re-heighting it would move every screen in the
                  product to fix one. */}
              <button
                className="btn btn-primary btn-commit"
                onClick={() => void submit()}
                disabled={
                  busy || (nameRequired && !name.trim()) || stagedPending === 0 || !!foreignImport
                }
              >
                {busy ? (
                  hasArchive ? (
                    "Apertura dello ZIP in corso…"
                  ) : (
                    "Caricamento in corso…"
                  )
                ) : stagedFailed > 0 ? (
                  `Riprova i ${stagedFailed} file non caricati`
                ) : (
                  <>
                    <I.Upload size={14} />{" "}
                    {stagedPending === 1
                      ? "Carica documenti"
                      : `Carica documenti (${stagedPending})`}
                  </>
                )}
              </button>
              <div style={{ fontSize: 11.5, color: "var(--c-ink-3)", flex: 1, minWidth: 220 }}>
                {!name.trim()
                  ? "Inserisci il nome del progetto per continuare."
                  : stagedPending === 0
                    ? "Aggiungi almeno un file per continuare."
                    : analyzing
                      ? "Carichiamo e riconosciamo i nuovi documenti. L'analisi già in corso non riparte."
                      : analysisFailed
                        ? /* Names no control, and no direction. Two reasons.
                             This bar sits OUTSIDE the tabpanel so it prints on
                             all three tabs, while «Riprova analisi» exists on
                             one at a time — and, worse, QA proved the sentence
                             can ONLY appear while that control is DISABLED:
                             it needs `stagedPending > 0`, and any staged file
                             makes `startBlockedBy` return "unsent". So every
                             version of this that named the button was telling
                             the reader to press something refused. What is
                             true is the sequence: these upload first, the
                             analysis restarts after, from the Esito tab. */
                          "Carichiamo e riconosciamo i nuovi documenti. L'analisi si rilancia dopo, da «Esito»."
                        : analysisDone
                          ? "Carichiamo e riconosciamo i nuovi documenti. L'analisi già conclusa non viene rifatta."
                          : "Creiamo il progetto, carichiamo i file e riconosciamo ogni documento. Poi controlli il riconoscimento e avvii tu l'analisi: non parte da sola."}
              </div>
            </div>
          )}

          {/* ── The one control that spends money ─────────────────────────────
              Sticky, and rendered for the WHOLE wizard rather than only beside
              the step it belongs to. That is the refusal's exit: when the server
              answers 422 the notice above says what is missing, and the way to
              try again is already on screen and cannot be scrolled away from.
              The defect this avoids is recorded twice in CLAUDE.md — a refusal
              whose remedy has no reachable control is terminal, and deleting a
              bad prediction does not by itself supply the missing exit.

              DISABLED while an upload is in flight (`busy`) or while any
              staged file has not been sent (`stagedPending`), and both were
              found by QA rather than designed in. `phase` reaches 2 as soon as
              the FIRST upload's refetch lands, so this bar rendered — enabled —
              while files 2..n were still going up. Measured: with the four
              required documents landed and a fifth still in flight, Avvia
              started a real 1-3 hour paid run on 4 of 5 documents, and the
              fifth arrived afterwards with nothing on screen saying it had
              missed the run. The server's pre-flight is no net here: it refuses
              only a missing REQUIRED role, and a portal folder delivers exactly
              the shape that defeats it — CME, EP, AP and Relazione first, then
              twenty-six drawings. Before `P-31` the window did not exist,
              because the start ran after the whole upload loop.

              The refusal keeps an exit, which is what makes disabling safe: the
              commit bar renders on `stagedPending > 0`, i.e. exactly when this
              button is disabled for that reason, and `busy` clears itself.

              Deliberately NOT disabled on a missing document. A client-side
              "are the four required roles present?" check broke three rounds
              running on this screen — a per-call set, then an accumulating ref,
              then that ref pruned against a react-query CACHE SNAPSHOT, which
              made a paid run unreachable and terminal. The server already
              answers 422 with the list. `canRunAnalysis` is a different thing
              and is fine to gate on: it is a fact about the CALLER, read from
              the token, not a prediction about server state that can go stale.
          */}
          {/* `P-39` `L2-05` — the action tray. In the prototype this is not a
              card at all: it is a SUNKEN grey sill welded to the foot of the
              list, square across the top, carrying the panel's own bottom
              corners and a border only on its top edge, with the shadow cast
              UPWARD so the rows appear to slide under it. Ours was a third
              white card with a full border, corners all round and a downward
              shadow, floating below the panel — three white slabs where the
              prototype composes one object.

              DIVERGENCE, stated rather than hidden: the fill, the corners, the
              single top border and the upward shadow are ported. The tray is
              NOT reparented into the panel card, which is what the prototype
              literally does, for two reasons that are properties of this code
              and not of the design. (1) ~475 lines of conditional content sit
              between the panel and this bar in the JSX, so welding them means
              moving a control that spends money past all of it. (2) It is
              `position: sticky`, and an ancestor that clips would kill that —
              the bar would scroll away with the page. So it reads as the sill
              without being its child. If those 475 lines ever move, revisit. */}
          {awaitingStart && (
            <div
              style={{
                padding: "14px 18px",
                position: "sticky",
                bottom: 0,
                background: "var(--c-bg-sunken)",
                /* `--c-line`, matching the prototype's `#e8e6e1` exactly. This
                   shipped as `--c-line-strong` (`#d6d3cc`) — a darker hairline
                   than either the prototype OR the `.card` border it replaced,
                   and it was not among the divergences the commit declared.
                   An undeclared divergence is the kind that becomes house
                   style by accident. */
                borderTop: "1px solid var(--c-line)",
                borderRadius: "0 0 var(--r-xl) var(--r-xl)",
                boxShadow: "0 -4px 12px rgba(20, 22, 28, 0.07)",
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              {step === 1 ? (
                <>
                  <button
                    className="btn btn-primary"
                    style={{ height: 36 }}
                    disabled={missingPriceBook}
                    onClick={() => setStep(2)}
                  >
                    {/* `P-37`/B3 — names the DESTINATION, and names it the same
                        way the tab above does. "Avanti: scegli la Knowledge
                        Base" described the errand; "Continua al passo 2" says
                        where you land, so the strip and the button agree. */}
                    Continua al passo 2 <I.Arrow size={13} />
                  </button>
                  {missingPriceBook ? (
                    /* `P-47`. The reason sits BESIDE the disabled control, not
                       behind a hover: a greyed button with no sentence is a
                       screen that refuses without saying why.

                       `disabled` is the affordance, never the rule — a
                       `disabled` attribute is not a guard, and this project has
                       twice written a rule into one and nowhere else. The rule
                       is enforced at `POST /analyze`, which is the only place
                       that spends money. */
                    <div
                      role="status"
                      style={{
                        fontSize: 11.5,
                        color: "var(--c-warn)",
                        flex: 1,
                        minWidth: 220,
                      }}
                    >
                      Manca il listino prezzi di riferimento: associane uno per proseguire.
                    </div>
                  ) : (
                    <div
                      style={{ fontSize: 11.5, color: "var(--c-ink-3)", flex: 1, minWidth: 220 }}
                    >
                      Controlla i documenti del computo qui sopra: un file riconosciuto male non
                      viene fermato da nessun controllo e falsa tutta l&apos;analisi. Niente è
                      ancora partito.
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* Frozen while a start is in flight. Passo 1's own sub-line
                      ends "Niente è ancora partito", and the status card reads
                      `jobId ? "In corso" : "Non ancora partita"` — `jobId` lands
                      only on the response — so leaving Passo 2 mid-request put
                      two agreeing false statements on screen with the POST open.
                      Nothing is at risk (the latch and `isPending` both survive
                      the step change); it is the screen that lies. */}
                  <button
                    className="btn"
                    style={{ height: 36 }}
                    onClick={() => setStep(1)}
                    disabled={startAnalysis.isPending || kb.isPending}
                  >
                    <span style={{ transform: "scaleX(-1)", display: "inline-flex" }}>
                      <I.Arrow size={13} />
                    </span>{" "}
                    Passo 1
                  </button>
                  <button
                    className="btn btn-primary"
                    style={{ height: 36 }}
                    onClick={() => void startRun(true)}
                    disabled={startBlocked || startAnalysis.isPending || kb.isPending}
                  >
                    <I.Sparkle size={14} />{" "}
                    {startAnalysis.isPending || kb.isPending ? "Avvio in corso…" : startLabel}
                  </button>
                  <div style={{ fontSize: 11.5, color: "var(--c-ink-3)", flex: 1, minWidth: 220 }}>
                    {startBlock === "role"
                      ? "Il tuo ruolo non permette di avviare un'analisi. Chiedi a un amministratore o a un editor del tuo studio."
                      : startBlock === "uploading"
                        ? "Stiamo ancora caricando i file. L'analisi può partire solo su documenti già arrivati."
                        : startBlock === "side-upload"
                          ? sideUploadSentence(listinoUpload, missingUploadState)
                          : startBlock === "unsent"
                            ? `${stagedPending} ${plural(stagedPending, "file non è ancora stato caricato", "file non sono ancora stati caricati")}: ${plural(stagedPending, "caricalo", "caricali")} con il pulsante qui sopra, oppure ${plural(stagedPending, "rimuovilo", "rimuovili")} dalla lista. L'analisi non ${plural(stagedPending, "lo includerebbe", "li includerebbe")}.`
                            : startBlock === "listino"
                              ? "Manca il listino prezzi di riferimento. Torna al passo 1 e caricane uno, oppure scegline uno dalla libreria."
                              : /* `kbBatchPending`, not `approve.length > 0`. With
                               every document vetoed the batch is pure declines —
                               still a real write — so with the filter on the
                               footer printed the SAME sentence as the unfiltered
                               case. Two different facts, one string: unfiltered
                               those declines are sent, filtered nothing leaves
                               the browser. */
                                onlyReview && kbBatchPending
                                ? /* Named, because a button that silently stopped
                                 doing half of what its old label promised is the
                                 dishonest version of this fix. */
                                  `Il filtro «Da confermare» è attivo, quindi la Knowledge Base non parte con questa analisi: ${kbAwaiting} ${plural(kbAwaiting, "documento resta", "documenti restano")} in attesa, da confermare nel Passo 2. L'analisi dura 1–3 ore e gira sul server: puoi chiudere il browser.`
                                : kbBatchPending
                                  ? `${kb.summary} L'analisi dura 1–3 ore e gira sul server: puoi chiudere il browser.`
                                  : "L'analisi dura 1–3 ore e gira sul server: puoi chiudere il browser."}
                  </div>
                </>
              )}
            </div>
          )}

          {/* `KB-22` — start the Knowledge Base and buy NO analysis. Platform
              administration only.

              **OUTSIDE the sticky Avvia bar, deliberately, and that is a fix
              rather than a layout preference.** It sat inside for one revision
              and two tests went red — correctly. `UploadView.p31r2.kb.test.tsx`
              reads the bar's whole text as the sub-line, on purpose ("read that
              way rather than by a class, so a restyle cannot silently empty this
              measurement"), and asserts that the label and the sub-line make the
              SAME claim about the Knowledge Base in every pressable state. A
              second control living in that bar breaks the invariant honestly:
              with a file still unsent, Avvia is blocked while this button is
              not, so the bar really would have claimed something about the KB in
              a state where the analysis cannot start. And `p31.lbl.test.tsx`
              found the other half — the note repeated `kb.summary`, printing the
              same sentence twice on one screen. Neither test was weakened; the
              control moved and the duplicate sentence was deleted.

              Which leaves the customer's bar byte-identical, which is what Omar
              asked for: *"I am not asking to change the production commercial
              flow."*

              **It knowingly overrides a rule stated twice in this codebase** —
              «two buttons that both spend money on the same screen, with the
              analysis attached to only one of them» (:2080-2088, and
              `IntakeReviewPanel.tsx:2153-2157`, which suppresses its own KB
              commit before a run for exactly this reason). Recorded as an
              override, not left looking like an oversight. It is justified only
              by WHO sees it.

              It calls `kb.submit()` and NOTHING else — no `beginAnalysis`, no
              `startingRef`, no `setStep`. There is no server work behind it:
              `POST …/documents/kb-forward` is already analysis-independent (its
              only guard checks the project exists) and documents are `pending`
              from upload, so this button IS the whole feature.

              NOT gated on `startBlocked`: every one of its causes is about the
              ANALYSIS — `role` is `analysis:run`, plus `listino`, `unsent`,
              `side-upload` — and refusing a Knowledge Base forward because a
              price book is missing would state a false reason. `busy` IS
              included: while an upload is in flight the document set is still
              arriving, and a partial send is worse than a wait.

              `!onlyReview` is included and is NOT cosmetic. The filter narrows
              what is on SCREEN; `kb.submit()` sends the whole batch regardless.
              Without it, pressing this while «Da confermare» is active would buy
              documents the person cannot see — the exact thing `kbRidesAlong`
              carries the same clause to prevent.

              **`step === 2` is the SAME argument through a different door, and
              the QA round caught me leaving it out.** `awaitingStart` is
              `phase === 2 && !jobId` — it says nothing about which STEP is on
              screen, and the sticky bar above branches on `step` internally
              (:4131). Without this clause the strip rendered on **Passo 1**,
              where `activeTab` is "1" and the Knowledge Base checkboxes are not
              mounted at all: one press would have bought a batch the person had
              never been shown. Identical hazard to the filter case, which is why
              it sits in the same predicate rather than somewhere cleverer.

              The cost is NOT restated here: the bar above already prints
              `kb.summary` under the same `kbBatchPending` condition, and saying
              it twice two lines apart is how a screen starts reading as noise. */}
          {awaitingStart &&
            step === 2 &&
            isPlatformAdmin &&
            isKbAdmin &&
            !onlyReview &&
            kbBatchPending && (
              <div
                className="card"
                style={{
                  padding: 12,
                  marginTop: 10,
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  flexWrap: "wrap",
                  borderStyle: "dashed",
                }}
              >
                <button
                  className="btn"
                  style={{ height: 36 }}
                  onClick={() => void kb.submit()}
                  disabled={kb.isPending || busy || startAnalysis.isPending}
                >
                  <I.Sparkle size={14} />{" "}
                  {kb.isPending ? "Invio…" : `Avvia solo la Knowledge Base (${kb.approve.length})`}
                </button>
                <div style={{ fontSize: 11.5, color: "var(--c-ink-3)", flex: 1, minWidth: 240 }}>
                  {/* `KB-23`. The count on the button is right and was never the
                      defect — the defect is that nothing on this screen pairs it
                      with two FILENAMES. A locked-in document has no row here at
                      all (`groupDocuments` files it under `required`, which the
                      "kb" view hides), so the only way to learn what the extra
                      number is made of was one 11px line further up the page.
                      Omar met that as «2 documenti inviati» after ticking one.
                      Named before the money is spent, not after. */}
                  {kb.lockedInApproved.length > 0 && (
                    <div style={{ marginBottom: 6 }}>
                      Sono {kb.approve.length}, non {kb.approve.length - kb.lockedInApproved.length}
                      :{" "}
                      <strong>
                        {kb.approve.length - kb.lockedInApproved.length}{" "}
                        {plural(
                          kb.approve.length - kb.lockedInApproved.length,
                          "scelto da te",
                          "scelti da te",
                        )}
                      </strong>
                      , più {kb.lockedInApproved.map((d) => d.filename).join(", ")} —{" "}
                      {plural(
                        kb.lockedInApproved.length,
                        "richiesto dall'analisi e sempre incluso",
                        "richiesti dall'analisi e sempre inclusi",
                      )}
                      .
                    </div>
                  )}
                  Invia i documenti alla Knowledge Base e{" "}
                  <strong>non avvia nessuna analisi del computo</strong>: nessuna analisi viene
                  acquistata. Controllo riservato all&apos;amministrazione della piattaforma — i
                  clienti non lo vedono.
                </div>
              </div>
            )}

          {/* `P-37`/A8 — "Cosa succede dopo il caricamento", in the main column
              where the person deciding whether to press the button is looking.
              It carried the same promise from the sidebar, which in Fase 1 sits
              beside the work rather than in it; a first-time user who never
              reads the right-hand column never learns that pressing the commit
              button does NOT start a 1–3 hour analysis. That is the single
              misunderstanding `P-31` exists to prevent, so it belongs on the
              path, not next to it.

              Collapsed by default, as in the prototype: it is reassurance, not
              instruction — nothing on this screen is blocked on reading it. */}
          {phase === 1 && (
            <div>
              <button
                className="btn btn-ghost"
                aria-expanded={explainerOpen}
                onClick={() => setExplainerOpen((v) => !v)}
                style={{
                  width: "100%",
                  justifyContent: "flex-start",
                  gap: 8,
                  color: "var(--c-ink-3)",
                  fontSize: 12.5,
                  minHeight: 44,
                }}
              >
                <span
                  style={{
                    display: "inline-flex",
                    transform: explainerOpen ? "rotate(180deg)" : "none",
                    transition: "transform var(--tr-fast)",
                  }}
                >
                  <I.ChevronD size={14} />
                </span>
                Cosa succede dopo il caricamento
              </button>
              {explainerOpen && (
                <div className="card" style={{ padding: "16px 18px", marginTop: 4 }}>
                  <p style={{ margin: "0 0 14px", fontSize: 12.5, color: "var(--c-ink-2)" }}>
                    {/* The two commitments were the wrong way round, and it got
                        the money press wrong: passo 1's only control is
                        «Continua al passo 2», beside "Niente è ancora partito";
                        the start lives on passo 2 and commits the Knowledge Base
                        in the same press. On a screen whose entire premise is
                        that the reader knows what starts a 1–3 hour paid run,
                        that is not a detail. The sidebar used to carry the
                        correct sentence unconditionally; `P-37` gated it to
                        `phase !== 1`, so Fase 1 lost the right one and kept
                        this. */}
                    Dopo il caricamento riconosciamo ogni documento e ti guidiamo in due passi: al
                    passo 1 confermi i 4 documenti richiesti (CME, Elenco Prezzi, Nuovi Prezzi,
                    Relazione) e il listino prezzi di riferimento; al passo 2 scegli i documenti per
                    la Knowledge Base e avvii l&apos;analisi. Senza listino l&apos;analisi non
                    pu&ograve; partire: puoi caricarlo o sceglierlo dalla libreria.
                  </p>
                  <ol
                    style={{
                      listStyle: "none",
                      margin: 0,
                      padding: 0,
                      display: "flex",
                      flexDirection: "column",
                      gap: 10,
                    }}
                  >
                    {PIPELINE_STEPS.map(([t, sub], i) => (
                      <li key={t} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                        <span
                          style={{
                            width: 18,
                            height: 18,
                            borderRadius: "50%",
                            border: "1px solid var(--c-line-strong)",
                            color: "var(--c-ink-3)",
                            fontSize: 10,
                            fontWeight: 600,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            marginTop: 1,
                          }}
                        >
                          {i + 1}
                        </span>
                        <span>
                          <span style={{ fontSize: 12.5, fontWeight: 500, display: "block" }}>
                            {t}
                          </span>
                          <span style={{ fontSize: 11, color: "var(--c-ink-3)" }}>{sub}</span>
                        </span>
                      </li>
                    ))}
                  </ol>
                  <p style={{ margin: "14px 0 0", fontSize: 11.5, color: "var(--c-ink-3)" }}>
                    Tempo stimato: <strong>1–3 ore</strong>. L&apos;analisi gira sul server: puoi
                    chiudere il browser.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* `P-46` — the «Pipeline AI» sidebar was deleted here, not hidden.
            Omar, 25 Aug, from `a_analysis.png`: Passo 1 is the documents and
            Avanzamento is the progress, and the target Avanzamento carries no
            side panel at all — its content belongs to the two lanes.

            What it held, and where each thing went. The analysis PROGRESS BAR
            (the wizard's only `role="progressbar"` for the analysis) moves into
            the Processo 1 lane below. The elapsed clock was already printed by
            that lane (`{progress}% · trascorso … · stimate 1–3 ore`), so it is
            not lost with the panel. The stateful `PIPELINE_STEPS` list is the
            same four stages the Avanzamento panel's own rail already draws.
            The pre-start "non parte da sola" paragraph is duplicated verbatim
            in the `awaitingStart` block, which is the only state it applied to
            — and deleting this copy removes one of the THREE «il listino
            regionale è facoltativo» claims `P-47` has to correct.

            CORRECTED 25 Aug, twice, and the correction is worth more than the
            number. This comment first said "one of the FOUR", copying the count
            from the roadmap's `P-47` row, which said «grepped, not estimated».
            It was neither: the fourth site (`UploadView.tsx:2424`) is a
            KNOWLEDGE BASE sentence — «La conferma per la Knowledge Base spetta
            a un amministratore. L'analisi parte comunque» — swept up by a grep
            loose enough to match «parte comunque». Then an independent auditor
            corrected it to TWO and concluded none now-false remain; that grep
            was case-SENSITIVE and missed `IntakeReviewPanel.tsx:1772`, which
            spells it «Facoltativo:» with a capital F.

            Measured, case-insensitively, across both files: THREE listino
            claims existed, this one was deleted by `P-46`, and TWO survived.

            CLOSED by `P-47` (25 Aug). Both surviving claims are now corrected —
            the Passo-1 explainer above and the price-list card in
            `IntakeReviewPanel`. Neither says the analysis starts without a
            listino, because it no longer does: the server refuses at
            `POST /analyze` and «Continua al passo 2» is disabled. The line
            numbers quoted above are from before those edits and are left as
            they were written; `grep -ni facoltativ src/pages/ingest/` is the
            check that does not go stale. */}
      </div>
      {refusedDocDelete !== null && (
        <AdminOnlyDeleteDialog
          subject={refusedDocDelete}
          onDismiss={() => setRefusedDocDelete(null)}
        />
      )}
    </div>
  );
}
